
import React, { useState, useEffect, useCallback } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const API = "https://slategroup.app";

const SEVERITY_COLOR = {
  CRITICAL: "#b91c1c", HIGH: "#c2410c",
  MEDIUM: "#a16207", LOW: "#15803d",
  UNKNOWN: "#6b7280", "NO DATA": "#9ca3af"
};
const SEVERITY_BG = {
  CRITICAL: "#fef2f2", HIGH: "#fff7ed",
  MEDIUM: "#fefce8", LOW: "#f0fdf4",
  UNKNOWN: "#f9fafb", "NO DATA": "#f9fafb"
};
const SEVERITY_BORDER = {
  CRITICAL: "#fecaca", HIGH: "#fed7aa",
  MEDIUM: "#fde68a", LOW: "#bbf7d0",
  UNKNOWN: "#e5e7eb", "NO DATA": "#e5e7eb"
};

const STATUS_LABELS = {
  unreviewed: "Unreviewed",
  in_progress: "In Progress",
  acknowledged: "Acknowledged",
  suppressed: "Suppressed"
};
const STATUS_COLOR = {
  unreviewed: "#6b7280",
  in_progress: "#2b7cd3",
  acknowledged: "#15803d",
  suppressed: "#9ca3af"
};

function riskLabel(score) {
  if (!score) return "NO DATA";
  if (score >= 9.0) return "CRITICAL";
  if (score >= 7.0) return "HIGH";
  if (score >= 4.0) return "MEDIUM";
  return "LOW";
}

function SeverityBadge({ label }) {
  const l = (label || "UNKNOWN").toUpperCase();
  return React.createElement("span", {
    style: {
      display: "inline-flex", alignItems: "center",
      padding: "2px 7px", borderRadius: 4,
      fontSize: 10, fontWeight: 600,
      background: SEVERITY_BG[l] || "#f9fafb",
      color: SEVERITY_COLOR[l] || "#6b7280",
      border: "1px solid " + (SEVERITY_BORDER[l] || "#e5e7eb"),
      whiteSpace: "nowrap"
    }
  }, l);
}

function StatusBadge({ status, onClick }) {
  return React.createElement("span", {
    onClick: onClick,
    style: {
      display: "inline-flex", alignItems: "center", gap: 3,
      padding: "2px 8px", borderRadius: 4,
      fontSize: 10, fontWeight: 600,
      background: "#f1f5f9",
      color: STATUS_COLOR[status] || "#6b7280",
      border: "1px solid #e2e8f0",
      cursor: onClick ? "pointer" : "default",
      whiteSpace: "nowrap"
    }
  }, STATUS_LABELS[status] || status);
}

function KEVBadge() {
  return React.createElement("span", {
    style: {
      display: "inline-flex", alignItems: "center", gap: 3,
      padding: "2px 7px", borderRadius: 4,
      fontSize: 10, fontWeight: 700,
      background: "#f5f3ff", color: "#7c3aed",
      border: "1px solid #ddd6fe", whiteSpace: "nowrap"
    }
  }, "⚡ KEV");
}

function RansomwareBadge() {
  return React.createElement("span", {
    style: {
      display: "inline-flex", alignItems: "center", gap: 3,
      padding: "2px 7px", borderRadius: 4,
      fontSize: 10, fontWeight: 700,
      background: "#fef2f2", color: "#b91c1c",
      border: "1px solid #fecaca", whiteSpace: "nowrap"
    }
  }, "🔴 Ransomware");
}

function StatCard({ label, value, icon, colorClass, trend, trendDir, onClick }) {
  const colors = {
    blue: { bg: "#eff6ff", color: "#2b7cd3", border: "#bfdbfe" },
    red: { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" },
    orange: { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa" },
    yellow: { bg: "#fefce8", color: "#a16207", border: "#fde68a" },
    purple: { bg: "#f5f3ff", color: "#7c3aed", border: "#ddd6fe" },
    green: { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" },
    gray: { bg: "#f9fafb", color: "#6b7280", border: "#e5e7eb" },
  };
  const c = colors[colorClass] || colors.blue;
  const trendColors = { up: "#b91c1c", down: "#15803d", flat: "#9ca3af", good: "#15803d" };

  return React.createElement("div", {
    style: {
      background: "#fff", border: "1px solid #e2e8f0",
      borderRadius: 12, padding: "14px 16px",
      boxShadow: "0 1px 3px rgba(0,0,0,.05)",
      cursor: "pointer", transition: "box-shadow .12s, transform .12s"
    },
    onClick: onClick,
    onMouseEnter: e => { if(onClick){ e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,.08)"; e.currentTarget.style.transform = "translateY(-1px)"; }},
    onMouseLeave: e => { e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,.05)"; e.currentTarget.style.transform = ""; }
  },
    React.createElement("div", { style: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 10 } },
      React.createElement("div", {
        style: { width: 30, height: 30, borderRadius: 7, background: c.bg, border: "1px solid " + c.border,
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: c.color }
      }, React.createElement("i", { className: "ti ti-" + icon })),
      trend && React.createElement("span", {
        style: { fontSize: 10, fontWeight: 500, padding: "2px 6px", borderRadius: 4,
          background: trendDir === "up" ? "#fef2f2" : trendDir === "good" ? "#f0fdf4" : "#f9fafb",
          color: trendColors[trendDir] || "#9ca3af" }
      }, trend)
    ),
    React.createElement("div", { style: { fontSize: 28, fontWeight: 600, color: "#0f1923", lineHeight: 1, fontFamily: "'DM Mono', monospace" } }, value),
    React.createElement("div", { style: { fontSize: 11, color: "#8a9ab0", marginTop: 3, fontWeight: 500, textTransform: "uppercase", letterSpacing: ".3px" } }, label)
  );
}

// Login Screen
function LoginScreen({ onLogin, onMFARequired }) {
  var us = useState(""); var username = us[0]; var setUsername = us[1];
  var ps = useState(""); var password = ps[0]; var setPassword = ps[1];
  var es = useState(""); var error = es[0]; var setError = es[1];
  var ls = useState(false); var loading = ls[0]; var setLoading = ls[1];

  function handleLogin() {
    setLoading(true); setError("");
    fetch(API + "/api/login", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password })
    })
    .then(r => { if (!r.ok) throw new Error(); return r.json(); })
    .then(d => {
      if (d.mfa_required) { onMFARequired(d.partial_token); }
      else {
        localStorage.setItem("sc_token", d.access_token);
        localStorage.setItem("sc_user", d.username);
        onLogin(d.access_token, d.username, d.mfa_setup_required);
      }
    })
    .catch(() => { setError("Invalid username or password"); setLoading(false); });
  }

  return React.createElement("div", {
    style: { minHeight: "100vh", background: "#f4f6f9", display: "flex", alignItems: "center", justifyContent: "center" }
  },
    React.createElement("div", {
      style: { background: "#fff", borderRadius: 16, padding: "40px 36px", width: 360,
        boxShadow: "0 4px 24px rgba(0,0,0,.08)", border: "1px solid #e2e8f0" }
    },
      React.createElement("div", { style: { textAlign: "center", marginBottom: 28 } },
        React.createElement("div", {
          style: { width: 44, height: 44, borderRadius: 10, background: "#1e3a5f",
            display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }
        },
          React.createElement("div", { style: { width: 8, height: 8, borderRadius: "50%", background: "#4ade80", boxShadow: "0 0 6px #4ade80" } })
        ),
        React.createElement("div", { style: { fontSize: 18, fontWeight: 600, color: "#0f1923" } }, "Slate Clarity"),
        React.createElement("div", { style: { fontSize: 12, color: "#8a9ab0", marginTop: 3 } }, "Network Intelligence Platform")
      ),
      error && React.createElement("div", {
        style: { background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8,
          padding: "9px 13px", marginBottom: 14, fontSize: 12, color: "#b91c1c" }
      }, error),
      ["Username", "Password"].map((label, i) =>
        React.createElement("div", { key: label, style: { marginBottom: 14 } },
          React.createElement("label", { style: { fontSize: 12, fontWeight: 500, color: "#374151", display: "block", marginBottom: 5 } }, label),
          React.createElement("input", {
            type: i === 1 ? "password" : "text",
            value: i === 0 ? username : password,
            onChange: e => i === 0 ? setUsername(e.target.value) : setPassword(e.target.value),
            onKeyDown: e => e.key === "Enter" && handleLogin(),
            placeholder: i === 0 ? "Enter username" : "Enter password",
            style: { width: "100%", padding: "9px 11px", borderRadius: 7, border: "1px solid #d1d5db",
              fontSize: 13, boxSizing: "border-box", outline: "none", fontFamily: "inherit" }
          })
        )
      ),
      React.createElement("button", {
        onClick: handleLogin, disabled: loading,
        style: { width: "100%", padding: "10px", borderRadius: 7, border: "none",
          background: "#2b7cd3", color: "#fff", fontSize: 13, fontWeight: 600,
          cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1, fontFamily: "inherit" }
      }, loading ? "Signing in..." : "Sign in"),
      React.createElement("div", { style: { textAlign: "center", marginTop: 14, fontSize: 11, color: "#9ca3af" } }, "Slate Group, LLC · Confidential")
    )
  );
}

// MFA Verify
function MFAVerifyScreen({ partialToken, onSuccess, onBack }) {
  var cs = useState(""); var code = cs[0]; var setCode = cs[1];
  var es = useState(""); var error = es[0]; var setError = es[1];
  var ls = useState(false); var loading = ls[0]; var setLoading = ls[1];

  function verify() {
    if (code.length !== 6) { setError("Enter 6-digit code"); return; }
    setLoading(true); setError("");
    fetch(API + "/api/mfa/verify", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code, partial_token: partialToken })
    })
    .then(r => { if (!r.ok) throw new Error(); return r.json(); })
    .then(d => { localStorage.setItem("sc_token", d.access_token); localStorage.setItem("sc_user", d.username); onSuccess(d.access_token, d.username); })
    .catch(() => { setError("Invalid code"); setLoading(false); setCode(""); });
  }

  return React.createElement("div", { style: { minHeight: "100vh", background: "#f4f6f9", display: "flex", alignItems: "center", justifyContent: "center" } },
    React.createElement("div", { style: { background: "#fff", borderRadius: 16, padding: "40px 36px", width: 360, boxShadow: "0 4px 24px rgba(0,0,0,.08)", border: "1px solid #e2e8f0" } },
      React.createElement("div", { style: { textAlign: "center", marginBottom: 24 } },
        React.createElement("div", { style: { fontSize: 18, fontWeight: 600, color: "#0f1923" } }, "Two-Factor Authentication"),
        React.createElement("div", { style: { fontSize: 12, color: "#8a9ab0", marginTop: 4 } }, "Enter the 6-digit code from your authenticator app")
      ),
      error && React.createElement("div", { style: { background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "9px 13px", marginBottom: 14, fontSize: 12, color: "#b91c1c" } }, error),
      React.createElement("input", {
        value: code, onChange: e => setCode(e.target.value.replace(/\D/g,"").slice(0,6)),
        onKeyDown: e => e.key === "Enter" && verify(),
        placeholder: "000000", maxLength: 6,
        style: { width: "100%", padding: "9px 11px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 20, textAlign: "center", letterSpacing: 8, fontFamily: "monospace", boxSizing: "border-box", marginBottom: 14, outline: "none" }
      }),
      React.createElement("button", { onClick: verify, disabled: loading, style: { width: "100%", padding: "10px", borderRadius: 7, border: "none", background: "#2b7cd3", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", marginBottom: 8 } }, loading ? "Verifying..." : "Verify"),
      React.createElement("button", { onClick: onBack, style: { width: "100%", padding: "10px", borderRadius: 7, border: "1px solid #e2e8f0", background: "#fff", color: "#6b7280", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" } }, "Back to login")
    )
  );
}

// MFA Setup
function MFASetupScreen({ token, onComplete, onSkip }) {
  var qs = useState(null); var qrCode = qs[0]; var setQrCode = qs[1];
  var ss = useState(""); var secret = ss[0]; var setSecret = ss[1];
  var cs = useState(""); var code = cs[0]; var setCode = cs[1];
  var es = useState(""); var error = es[0]; var setError = es[1];
  var ls = useState(false); var loading = ls[0]; var setLoading = ls[1];
  var vs = useState(false); var verified = vs[0]; var setVerified = vs[1];

  useEffect(function() {
    fetch(API + "/api/mfa/setup", { headers: { "Authorization": "Bearer " + token } })
      .then(r => r.json()).then(d => { setQrCode(d.qr_code); setSecret(d.secret); });
  }, []);

  function verify() {
    if (code.length !== 6) { setError("Enter 6-digit code"); return; }
    setLoading(true); setError("");
    fetch(API + "/api/mfa/setup/verify", {
      method: "POST", headers: { "Content-Type": "application/json", "Authorization": "Bearer " + token },
      body: JSON.stringify({ code })
    })
    .then(r => { if (!r.ok) throw new Error(); return r.json(); })
    .then(() => { setVerified(true); setLoading(false); })
    .catch(() => { setError("Invalid code"); setLoading(false); setCode(""); });
  }

  if (verified) return React.createElement("div", { style: { minHeight: "100vh", background: "#f4f6f9", display: "flex", alignItems: "center", justifyContent: "center" } },
    React.createElement("div", { style: { background: "#fff", borderRadius: 16, padding: "40px 36px", width: 360, textAlign: "center", boxShadow: "0 4px 24px rgba(0,0,0,.08)" } },
      React.createElement("div", { style: { fontSize: 40, marginBottom: 12 } }, "✓"),
      React.createElement("div", { style: { fontSize: 16, fontWeight: 600, marginBottom: 8 } }, "MFA Enabled"),
      React.createElement("div", { style: { fontSize: 13, color: "#6b7280", marginBottom: 20 } }, "Your account is now protected with two-factor authentication."),
      React.createElement("button", { onClick: onComplete, style: { width: "100%", padding: "10px", borderRadius: 7, border: "none", background: "#2b7cd3", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" } }, "Continue to dashboard")
    )
  );

  return React.createElement("div", { style: { minHeight: "100vh", background: "#f4f6f9", display: "flex", alignItems: "center", justifyContent: "center" } },
    React.createElement("div", { style: { background: "#fff", borderRadius: 16, padding: "40px 36px", width: 380, boxShadow: "0 4px 24px rgba(0,0,0,.08)" } },
      React.createElement("div", { style: { textAlign: "center", marginBottom: 20 } },
        React.createElement("div", { style: { fontSize: 16, fontWeight: 600 } }, "Set Up Two-Factor Authentication"),
        React.createElement("div", { style: { fontSize: 12, color: "#8a9ab0", marginTop: 4 } }, "Scan with Google Authenticator or Authy")
      ),
      qrCode && React.createElement("div", { style: { textAlign: "center", marginBottom: 14 } },
        React.createElement("img", { src: "data:image/png;base64," + qrCode, alt: "QR Code", style: { width: 160, height: 160, border: "1px solid #e2e8f0", borderRadius: 8 } })
      ),
      secret && React.createElement("div", { style: { background: "#f8fafc", borderRadius: 7, padding: "8px 12px", marginBottom: 14, textAlign: "center" } },
        React.createElement("div", { style: { fontSize: 10, color: "#9ca3af", marginBottom: 3 } }, "Manual entry key"),
        React.createElement("div", { style: { fontFamily: "monospace", fontSize: 12, letterSpacing: 2 } }, secret)
      ),
      error && React.createElement("div", { style: { background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 7, padding: "8px 12px", marginBottom: 12, fontSize: 12, color: "#b91c1c" } }, error),
      React.createElement("input", {
        value: code, onChange: e => setCode(e.target.value.replace(/\D/g,"").slice(0,6)),
        placeholder: "Enter 6-digit code to confirm", maxLength: 6,
        style: { width: "100%", padding: "9px 11px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 13, boxSizing: "border-box", marginBottom: 10, outline: "none", fontFamily: "inherit" }
      }),
      React.createElement("button", { onClick: verify, disabled: loading, style: { width: "100%", padding: "10px", borderRadius: 7, border: "none", background: "#2b7cd3", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", marginBottom: 8 } }, loading ? "Verifying..." : "Enable MFA"),
      React.createElement("button", { onClick: onSkip, style: { width: "100%", padding: "10px", borderRadius: 7, border: "1px solid #e2e8f0", background: "#fff", color: "#6b7280", fontSize: 13, cursor: "pointer", fontFamily: "inherit" } }, "Skip for now")
    )
  );
}

// Triage Modal
function TriageModal({ vuln, token, onClose, onSaved }) {
  var ss = useState(vuln.status || "unreviewed"); var status = ss[0]; var setStatus = ss[1];
  var ns = useState(vuln.status_note || ""); var note = ns[0]; var setNote = ns[1];
  var ls = useState(false); var saving = ls[0]; var setSaving = ls[1];

  function save() {
    setSaving(true);
    fetch(API + "/api/vulnerabilities/" + vuln.vuln_id + "/triage", {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "Authorization": "Bearer " + token },
      body: JSON.stringify({ status, note })
    })
    .then(r => r.json())
    .then(() => { setSaving(false); onSaved(); onClose(); })
    .catch(() => setSaving(false));
  }

  return React.createElement("div", {
    style: { position: "fixed", inset: 0, background: "rgba(0,0,0,.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
    onClick: e => e.target === e.currentTarget && onClose()
  },
    React.createElement("div", { style: { background: "#fff", borderRadius: 12, padding: 24, width: 420, boxShadow: "0 8px 32px rgba(0,0,0,.12)" } },
      React.createElement("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 } },
        React.createElement("div", { style: { fontSize: 14, fontWeight: 600, color: "#0f1923" } }, "Triage Vulnerability"),
        React.createElement("button", { onClick: onClose, style: { background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 18 } }, "×")
      ),
      React.createElement("div", { style: { background: "#f8fafc", borderRadius: 8, padding: "10px 12px", marginBottom: 16 } },
        React.createElement("div", { style: { fontFamily: "monospace", fontSize: 12, color: "#2b7cd3", marginBottom: 3 } }, vuln.cve_id),
        React.createElement("div", { style: { fontSize: 12, color: "#4a5568" } }, (vuln.description || "").slice(0, 120) + "...")
      ),
      React.createElement("div", { style: { marginBottom: 14 } },
        React.createElement("label", { style: { fontSize: 12, fontWeight: 500, color: "#374151", display: "block", marginBottom: 5 } }, "Status"),
        React.createElement("select", {
          value: status, onChange: e => setStatus(e.target.value),
          style: { width: "100%", padding: "8px 10px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 13, fontFamily: "inherit", outline: "none", background: "#fff" }
        },
          Object.entries(STATUS_LABELS).map(([val, label]) =>
            React.createElement("option", { key: val, value: val }, label)
          )
        )
      ),
      React.createElement("div", { style: { marginBottom: 18 } },
        React.createElement("label", { style: { fontSize: 12, fontWeight: 500, color: "#374151", display: "block", marginBottom: 5 } }, "Note (optional)"),
        React.createElement("textarea", {
          value: note, onChange: e => setNote(e.target.value),
          placeholder: "e.g. Accepted risk — device not internet-facing. Patching scheduled for Q3.",
          rows: 3,
          style: { width: "100%", padding: "8px 10px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 12, fontFamily: "inherit", outline: "none", resize: "vertical", boxSizing: "border-box" }
        })
      ),
      React.createElement("div", { style: { display: "flex", gap: 8 } },
        React.createElement("button", { onClick: onClose, style: { flex: 1, padding: "9px", borderRadius: 7, border: "1px solid #e2e8f0", background: "#fff", color: "#6b7280", fontSize: 13, cursor: "pointer", fontFamily: "inherit" } }, "Cancel"),
        React.createElement("button", { onClick: save, disabled: saving, style: { flex: 1, padding: "9px", borderRadius: 7, border: "none", background: "#2b7cd3", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" } }, saving ? "Saving..." : "Save")
      )
    )
  );
}


function ShadowAITab({ token }) {
  var ds = useState(null); var data = ds[0]; var setData = ds[1];
  var ls = useState(true); var loading = ls[0]; var setLoading = ls[1];

  useEffect(function() {
    fetch(API + "/api/shadow-ai", { headers: { "Authorization": "Bearer " + token } })
      .then(function(r) { return r.json(); })
      .then(function(d) { setData(d); setLoading(false); })
      .catch(function() { setLoading(false); });
  }, []);

  var RC = {
    critical: { bg:"#fef2f2", color:"#b91c1c", border:"#fecaca" },
    high:     { bg:"#fff7ed", color:"#c2410c", border:"#fed7aa" },
    medium:   { bg:"#fefce8", color:"#a16207", border:"#fde68a" },
    low:      { bg:"#f0fdf4", color:"#15803d", border:"#bbf7d0" },
  };

  var REACHABLE = [
    { name:"ChatGPT / OpenAI",    risk:"high",   icon:"message-circle-2", cat:"Generative AI" },
    { name:"Claude / Anthropic",  risk:"medium", icon:"message-circle-2", cat:"Generative AI" },
    { name:"Google Gemini",       risk:"high",   icon:"sparkles",         cat:"Generative AI" },
    { name:"Microsoft Copilot",   risk:"low",    icon:"brand-windows",    cat:"Generative AI" },
    { name:"Grammarly AI",        risk:"high",   icon:"writing",          cat:"AI Writing" },
    { name:"GitHub Copilot",      risk:"medium", icon:"code",             cat:"AI Coding" },
    { name:"Midjourney",          risk:"medium", icon:"photo",            cat:"Image Gen" },
    { name:"Perplexity AI",       risk:"medium", icon:"search",           cat:"Generative AI" },
    { name:"ElevenLabs",          risk:"high",   icon:"microphone",       cat:"AI Audio" },
    { name:"Hugging Face",        risk:"medium", icon:"cpu",              cat:"AI Platform" },
    { name:"Character.ai",        risk:"high",   icon:"user-circle",      cat:"Generative AI" },
    { name:"Replicate",           risk:"medium", icon:"refresh",          cat:"AI Platform" },
  ];

  if (loading) return React.createElement("div", { style:{ padding:40, textAlign:"center", color:"#8a9ab0" } }, "Loading Shadow AI data...");

  var summary = (data && data.summary) || {};
  var detections = (data && data.detections) || [];

  return React.createElement("div", null,

    React.createElement("div", { style:{ marginBottom:20 } },
      React.createElement("div", { style:{ fontSize:16, fontWeight:600, color:"#0f1923", marginBottom:4 } }, "Shadow AI Monitor"),
      React.createElement("div", { style:{ fontSize:12, color:"#8a9ab0" } },
        "Detects unauthorized AI tool usage from network DNS traffic. Passive visibility only — no blocking. Scans every 4 hours."
      )
    ),

    React.createElement("div", { style:{ background:"#fffbeb", border:"1px solid #fde68a", borderLeft:"4px solid #a16207", borderRadius:10, padding:"12px 16px", marginBottom:16, display:"flex", alignItems:"flex-start", gap:12 } },
      React.createElement("i", { className:"ti ti-info-circle", style:{ fontSize:18, color:"#a16207", flexShrink:0, marginTop:1 } }),
      React.createElement("div", null,
        React.createElement("div", { style:{ fontSize:12, fontWeight:600, color:"#a16207", marginBottom:3 } }, "What is Shadow AI?"),
        React.createElement("div", { style:{ fontSize:12, color:"#78350f", lineHeight:1.5 } },
          "Employees using AI tools without IT knowledge creates data governance risk. Patient records submitted to ChatGPT may violate HIPAA. " +
          "Client documents sent to AI tools may breach confidentiality. Proprietary code shared with Copilot may leak IP. " +
          "Slate Clarity detects AI tool usage from passive DNS monitoring — no agents required, no content captured."
        )
      )
    ),

    React.createElement("div", { style:{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:20 } },
      [
        { label:"AI Tools Detected",  value: summary.unique_tools || 0,      icon:"robot",          color: (summary.unique_tools||0) > 0 ? "#c2410c" : "#15803d" },
        { label:"Affected Devices",   value: summary.affected_devices || 0,  icon:"device-desktop", color: (summary.affected_devices||0) > 0 ? "#c2410c" : "#15803d" },
        { label:"High Risk Tools",    value: summary.high_risk || 0,         icon:"alert-triangle",  color: (summary.high_risk||0) > 0 ? "#b91c1c" : "#15803d" },
        { label:"Total Detections",   value: summary.total || 0,             icon:"eye",             color:"#2b7cd3" },
      ].map(function(s,i) {
        return React.createElement("div", { key:i, style:{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:10, padding:"14px 16px", boxShadow:"0 1px 3px rgba(0,0,0,.04)" } },
          React.createElement("i", { className:"ti ti-"+s.icon, style:{ fontSize:18, color:s.color, display:"block", marginBottom:8 } }),
          React.createElement("div", { style:{ fontSize:26, fontWeight:600, color:"#0f1923", fontFamily:"monospace" } }, s.value),
          React.createElement("div", { style:{ fontSize:11, color:"#8a9ab0", marginTop:3 } }, s.label)
        );
      })
    ),

    detections.length > 0 ?
      React.createElement("div", { style:{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:12, boxShadow:"0 1px 3px rgba(0,0,0,.04)" } },
        React.createElement("div", { style:{ padding:"14px 18px 0" } },
          React.createElement("div", { style:{ fontSize:13, fontWeight:600 } }, "Detections — " + detections.length + " found")
        ),
        React.createElement("div", { style:{ padding:"12px 18px 18px", overflowX:"auto" } },
          React.createElement("table", { style:{ width:"100%", borderCollapse:"collapse" } },
            React.createElement("thead", null,
              React.createElement("tr", null,
                ["Device","Tool","Risk","Domain","First Seen","Last Seen","Count","Policy Concern"].map(function(h) {
                  return React.createElement("th", { key:h, style:{ textAlign:"left", fontSize:10, fontWeight:600, color:"#8a9ab0", textTransform:"uppercase", letterSpacing:".4px", padding:"0 8px 8px", borderBottom:"1px solid #f1f5f9", whiteSpace:"nowrap" } }, h);
                })
              )
            ),
            React.createElement("tbody", null,
              detections.map(function(d,i) {
                var rc = RC[d.risk_level] || RC.medium;
                return React.createElement("tr", { key:i,
                  onMouseEnter:function(e){e.currentTarget.style.background="#f8fafc";},
                  onMouseLeave:function(e){e.currentTarget.style.background="";}
                },
                  React.createElement("td", { style:{ padding:"9px 8px", fontFamily:"monospace", fontSize:11 } }, String(d.asset_ip)),
                  React.createElement("td", { style:{ padding:"9px 8px", fontSize:12, fontWeight:600 } }, d.tool_name),
                  React.createElement("td", { style:{ padding:"9px 8px" } },
                    React.createElement("span", { style:{ fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:4, background:rc.bg, color:rc.color, border:"1px solid "+rc.border, textTransform:"uppercase" } }, d.risk_level)
                  ),
                  React.createElement("td", { style:{ padding:"9px 8px", fontFamily:"monospace", fontSize:10, color:"#2b7cd3" } }, d.domain_detected),
                  React.createElement("td", { style:{ padding:"9px 8px", fontSize:10, color:"#8a9ab0" } }, d.first_detected ? new Date(d.first_detected).toLocaleDateString() : "—"),
                  React.createElement("td", { style:{ padding:"9px 8px", fontSize:10, color:"#8a9ab0" } }, d.last_detected ? new Date(d.last_detected).toLocaleDateString() : "—"),
                  React.createElement("td", { style:{ padding:"9px 8px", textAlign:"center", fontFamily:"monospace", fontSize:11 } }, d.detection_count),
                  React.createElement("td", { style:{ padding:"9px 8px", fontSize:11, color:"#4a5568", maxWidth:220, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" } }, d.policy_concern)
                );
              })
            )
          )
        )
      )
    :
      React.createElement("div", null,
        React.createElement("div", { style:{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:12, padding:"32px", textAlign:"center", boxShadow:"0 1px 3px rgba(0,0,0,.04)", marginBottom:16 } },
          React.createElement("i", { className:"ti ti-robot", style:{ fontSize:40, color:"#e2e8f0", display:"block", marginBottom:12 } }),
          React.createElement("div", { style:{ fontSize:14, fontWeight:600, color:"#0f1923", marginBottom:6 } }, "No Shadow AI detections yet"),
          React.createElement("div", { style:{ fontSize:12, color:"#8a9ab0", maxWidth:400, margin:"0 auto" } },
            "Detections appear when devices actively query AI tool domains. Run scans during business hours for best coverage. Scanner checks every 4 hours automatically."
          )
        ),
        React.createElement("div", { style:{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:12, padding:"18px", boxShadow:"0 1px 3px rgba(0,0,0,.04)" } },
          React.createElement("div", { style:{ fontSize:13, fontWeight:600, marginBottom:4 } }, "AI Tools Reachable From This Network"),
          React.createElement("div", { style:{ fontSize:11, color:"#8a9ab0", marginBottom:14 } }, "20 tools confirmed reachable. Any device could be using them."),
          React.createElement("div", { style:{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8 } },
            REACHABLE.map(function(tool,i) {
              var rc = RC[tool.risk] || RC.medium;
              return React.createElement("div", { key:i, style:{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px", borderRadius:8, border:"1px solid #f1f5f9", background:"#fafafa" } },
                React.createElement("i", { className:"ti ti-"+tool.icon, style:{ fontSize:16, color:rc.color, flexShrink:0 } }),
                React.createElement("div", { style:{ flex:1, minWidth:0 } },
                  React.createElement("div", { style:{ fontSize:12, fontWeight:500, color:"#0f1923" } }, tool.name),
                  React.createElement("div", { style:{ fontSize:10, color:"#8a9ab0" } }, tool.cat)
                ),
                React.createElement("span", { style:{ fontSize:9, fontWeight:700, padding:"2px 5px", borderRadius:3, background:rc.bg, color:rc.color, border:"1px solid "+rc.border, textTransform:"uppercase", flexShrink:0 } }, tool.risk)
              );
            })
          )
        )
      )
  );
}


function CVEDetailPanel({ vuln, onClose }) {
  if (!vuln) return null;

  var RC = {
    CRITICAL: { bg:"#fef2f2", color:"#b91c1c", border:"#fecaca" },
    HIGH:     { bg:"#fff7ed", color:"#c2410c", border:"#fed7aa" },
    MEDIUM:   { bg:"#fefce8", color:"#a16207", border:"#fde68a" },
    LOW:      { bg:"#f0fdf4", color:"#15803d", border:"#bbf7d0" },
  };
  var sev = (vuln.severity || "UNKNOWN").toUpperCase();
  var rc = RC[sev] || { bg:"#f9fafb", color:"#6b7280", border:"#e5e7eb" };
  var nvdUrl = "https://nvd.nist.gov/vuln/detail/" + vuln.cve_id;

  return React.createElement("div", null,
    // Backdrop
    React.createElement("div", {
      onClick: onClose,
      style: { position:"fixed", inset:0, background:"rgba(0,0,0,.3)", zIndex:900 }
    }),

    // Panel
    React.createElement("div", {
      style: {
        position:"fixed", right:0, top:0, bottom:0, width:520,
        background:"#fff", boxShadow:"-4px 0 24px rgba(0,0,0,.12)",
        zIndex:901, display:"flex", flexDirection:"column",
        fontFamily:"'DM Sans',sans-serif"
      }
    },
      // Header
      React.createElement("div", { style:{ background:"#1e3a5f", padding:"18px 20px", flexShrink:0 } },
        React.createElement("div", { style:{ display:"flex", alignItems:"flex-start", justifyContent:"space-between" } },
          React.createElement("div", null,
            React.createElement("div", { style:{ fontFamily:"monospace", fontSize:16, fontWeight:700, color:"#fff", marginBottom:4 } }, vuln.cve_id),
            React.createElement("div", { style:{ fontSize:12, color:"#94a3b8" } }, "Vulnerability Detail")
          ),
          React.createElement("button", {
            onClick: onClose,
            style:{ background:"rgba(255,255,255,.1)", border:"none", color:"#fff", width:28, height:28, borderRadius:6, cursor:"pointer", fontSize:16, display:"flex", alignItems:"center", justifyContent:"center" }
          }, "×")
        )
      ),

      // Content
      React.createElement("div", { style:{ flex:1, overflowY:"auto", padding:20 } },

        // Risk score + badges
        React.createElement("div", { style:{ display:"flex", alignItems:"center", gap:10, marginBottom:20 } },
          React.createElement("div", { style:{ fontSize:36, fontWeight:700, fontFamily:"monospace", color:rc.color } }, vuln.cvss_score),
          React.createElement("div", null,
            React.createElement("div", { style:{ fontSize:11, color:"#8a9ab0", marginBottom:4 } }, "CVSS Score"),
            React.createElement("span", { style:{ fontSize:11, fontWeight:700, padding:"3px 9px", borderRadius:5, background:rc.bg, color:rc.color, border:"1px solid "+rc.border } }, sev)
          ),
          vuln.is_kev && React.createElement("span", { style:{ fontSize:11, fontWeight:700, padding:"3px 9px", borderRadius:5, background:"#f5f3ff", color:"#7c3aed", border:"1px solid #ddd6fe" } }, "⚡ KEV — Actively Exploited"),
          vuln.kev_ransomware === "Known" && React.createElement("span", { style:{ fontSize:11, fontWeight:700, padding:"3px 9px", borderRadius:5, background:"#fef2f2", color:"#b91c1c", border:"1px solid #fecaca" } }, "🔴 Ransomware")
        ),

        // Affected device
        React.createElement("div", { style:{ background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:8, padding:"12px 14px", marginBottom:16 } },
          React.createElement("div", { style:{ fontSize:10, fontWeight:600, color:"#8a9ab0", textTransform:"uppercase", letterSpacing:".4px", marginBottom:6 } }, "Affected Device"),
          React.createElement("div", { style:{ fontFamily:"monospace", fontSize:14, fontWeight:600, color:"#1e3a5f" } }, vuln.ip_address)
        ),

        // Description
        React.createElement("div", { style:{ marginBottom:16 } },
          React.createElement("div", { style:{ fontSize:11, fontWeight:600, color:"#374151", marginBottom:6, textTransform:"uppercase", letterSpacing:".4px" } }, "Description"),
          React.createElement("div", { style:{ fontSize:13, color:"#4a5568", lineHeight:1.6, background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:8, padding:"12px 14px" } }, vuln.description || "No description available.")
        ),

        // Required Action
        vuln.kev_required_action && React.createElement("div", { style:{ marginBottom:16 } },
          React.createElement("div", { style:{ fontSize:11, fontWeight:600, color:"#374151", marginBottom:6, textTransform:"uppercase", letterSpacing:".4px" } }, "Required Action (CISA)"),
          React.createElement("div", { style:{ fontSize:13, color:"#4a5568", lineHeight:1.6, background:"#fefce8", border:"1px solid #fde68a", borderRadius:8, padding:"12px 14px" } }, vuln.kev_required_action)
        ),

        // Timeline
        React.createElement("div", { style:{ background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:8, padding:"12px 14px", marginBottom:16 } },
          React.createElement("div", { style:{ fontSize:10, fontWeight:600, color:"#8a9ab0", textTransform:"uppercase", letterSpacing:".4px", marginBottom:10 } }, "Detection Timeline"),
          React.createElement("div", { style:{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 } },
            React.createElement("div", null,
              React.createElement("div", { style:{ fontSize:10, color:"#8a9ab0", marginBottom:3 } }, "First Detected"),
              React.createElement("div", { style:{ fontSize:13, fontWeight:500, color:"#0f1923" } }, vuln.first_detected ? new Date(vuln.first_detected).toLocaleDateString("en-US", {year:"numeric",month:"short",day:"numeric"}) : "—")
            ),
            React.createElement("div", null,
              React.createElement("div", { style:{ fontSize:10, color:"#8a9ab0", marginBottom:3 } }, "Last Detected"),
              React.createElement("div", { style:{ fontSize:13, fontWeight:500, color:"#0f1923" } }, vuln.last_detected ? new Date(vuln.last_detected).toLocaleDateString("en-US", {year:"numeric",month:"short",day:"numeric"}) : "—")
            ),
            React.createElement("div", null,
              React.createElement("div", { style:{ fontSize:10, color:"#8a9ab0", marginBottom:3 } }, "Clean Scans"),
              React.createElement("div", { style:{ fontSize:13, fontWeight:500, color:"#0f1923" } }, (vuln.consecutive_clean_scans || 0) + " of 3 required")
            ),
            React.createElement("div", null,
              React.createElement("div", { style:{ fontSize:10, color:"#8a9ab0", marginBottom:3 } }, "Status"),
              React.createElement("div", { style:{ fontSize:13, fontWeight:500, color: vuln.auto_closed ? "#15803d" : "#c2410c" } }, vuln.auto_closed ? "✓ Auto-closed" : "Active")
            )
          )
        ),

        // KEV details
        vuln.is_kev && React.createElement("div", { style:{ background:"#f5f3ff", border:"1px solid #ddd6fe", borderRadius:8, padding:"12px 14px", marginBottom:16 } },
          React.createElement("div", { style:{ fontSize:10, fontWeight:600, color:"#7c3aed", textTransform:"uppercase", letterSpacing:".4px", marginBottom:10 } }, "CISA KEV Details"),
          React.createElement("div", { style:{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 } },
            React.createElement("div", null,
              React.createElement("div", { style:{ fontSize:10, color:"#8a9ab0", marginBottom:3 } }, "Date Added to KEV"),
              React.createElement("div", { style:{ fontSize:13, fontWeight:500, color:"#0f1923" } }, vuln.kev_date_added || "—")
            ),
            React.createElement("div", null,
              React.createElement("div", { style:{ fontSize:10, color:"#8a9ab0", marginBottom:3 } }, "CISA Due Date"),
              React.createElement("div", { style:{ fontSize:13, fontWeight:500, color:"#b91c1c" } }, vuln.kev_due_date || "—")
            ),
            React.createElement("div", null,
              React.createElement("div", { style:{ fontSize:10, color:"#8a9ab0", marginBottom:3 } }, "Ransomware Campaign"),
              React.createElement("div", { style:{ fontSize:13, fontWeight:500, color: vuln.kev_ransomware === "Known" ? "#b91c1c" : "#6b7280" } }, vuln.kev_ransomware || "Unknown")
            )
          )
        ),

        // Actions
        React.createElement("div", { style:{ display:"flex", gap:8, marginTop:8 } },
          React.createElement("a", {
            href: nvdUrl, target:"_blank", rel:"noopener noreferrer",
            style:{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", gap:6, padding:"9px", borderRadius:7, border:"1px solid #e2e8f0", background:"#fff", color:"#2b7cd3", fontSize:12, fontWeight:500, textDecoration:"none" }
          },
            React.createElement("i", { className:"ti ti-external-link", style:{ fontSize:13 } }),
            "View on NVD"
          ),
          React.createElement("a", {
            href: "https://www.cisa.gov/known-exploited-vulnerabilities-catalog", target:"_blank", rel:"noopener noreferrer",
            style:{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", gap:6, padding:"9px", borderRadius:7, border:"1px solid #ddd6fe", background:"#f5f3ff", color:"#7c3aed", fontSize:12, fontWeight:500, textDecoration:"none" }
          },
            React.createElement("i", { className:"ti ti-flame", style:{ fontSize:13 } }),
            "CISA KEV Catalog"
          )
        )
      )
    )
  );
}

// Main Dashboard
function SettingsPage({ token }) {
  var es = React.useState({ smtp_host:"smtp.gmail.com", smtp_port:"587", smtp_user:"", alert_email:"", digest_cadence:"weekly", digest_day:"monday", digest_time:"07:00", alert_kev:true, alert_critical:true, alert_new_device:true, report_type:"technical", attach_pdf:true });
  var settings = es[0]; var setSettings = es[1];
  var ss = React.useState(false); var saving = ss[0]; var setSaving = ss[1];
  var ms = React.useState(""); var message = ms[0]; var setMessage = ms[1];
  function update(key, val) { setSettings(function(p){ var n=Object.assign({},p); n[key]=val; return n; }); }
  function save() { setSaving(true); setTimeout(function(){ setSaving(false); setMessage("Settings saved"); setTimeout(function(){ setMessage(""); },3000); },800); }
  var card = { background:"#fff", border:"1px solid #e2e8f0", borderRadius:12, padding:"20px 24px", marginBottom:16, boxShadow:"0 1px 3px rgba(0,0,0,.04)" };
  var lbl = { fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:5 };
  var inp = { width:"100%", padding:"8px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" };
  function Toggle(on, onClick) {
    return React.createElement("div", { onClick:onClick, style:{ width:40, height:22, borderRadius:999, background:on?"#2b7cd3":"#d1d5db", cursor:"pointer", position:"relative", transition:"background .2s", flexShrink:0 } },
      React.createElement("div", { style:{ position:"absolute", top:3, left:on?21:3, width:16, height:16, borderRadius:"50%", background:"#fff", transition:"left .2s" } })
    );
  }
  return React.createElement("div", { style:{ maxWidth:720 } },
    React.createElement("div", { style:{ marginBottom:20 } },
      React.createElement("div", { style:{ fontSize:16, fontWeight:600, color:"#0f1923", marginBottom:4 } }, "Settings"),
      React.createElement("div", { style:{ fontSize:12, color:"#8a9ab0" } }, "Configure alerts, notifications, and organization preferences")
    ),
    message && React.createElement("div", { style:{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, padding:"10px 14px", marginBottom:16, fontSize:12, color:"#15803d", fontWeight:500 } }, message),
    React.createElement("div", { style:card },
      React.createElement("div", { style:{ fontSize:14, fontWeight:600, color:"#0f1923", marginBottom:4 } }, "Email Configuration"),
      React.createElement("div", { style:{ fontSize:12, color:"#8a9ab0", marginBottom:16 } }, "SMTP settings for alert delivery"),
      React.createElement("div", { style:{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 } },
        React.createElement("div", null, React.createElement("label", { style:lbl }, "SMTP Host"), React.createElement("input", { style:inp, value:settings.smtp_host, onChange:function(e){update("smtp_host",e.target.value);} })),
        React.createElement("div", null, React.createElement("label", { style:lbl }, "SMTP Port"), React.createElement("input", { style:inp, value:settings.smtp_port, onChange:function(e){update("smtp_port",e.target.value);} })),
        React.createElement("div", null, React.createElement("label", { style:lbl }, "SMTP Username"), React.createElement("input", { style:inp, value:settings.smtp_user, onChange:function(e){update("smtp_user",e.target.value);}, placeholder:"alerts@yourdomain.com" })),
        React.createElement("div", null, React.createElement("label", { style:lbl }, "Alert Recipient"), React.createElement("input", { style:inp, value:settings.alert_email, onChange:function(e){update("alert_email",e.target.value);}, placeholder:"itdirector@company.com" }))
      )
    ),
    React.createElement("div", { style:card },
      React.createElement("div", { style:{ fontSize:14, fontWeight:600, color:"#0f1923", marginBottom:4 } }, "Alert Preferences"),
      React.createElement("div", { style:{ fontSize:12, color:"#8a9ab0", marginBottom:16 } }, "Choose which events trigger immediate email alerts"),
      React.createElement("div", { style:{ display:"flex", flexDirection:"column", gap:10 } },
        [{key:"alert_kev",label:"KEV Match",desc:"Actively exploited vulnerability detected"},
         {key:"alert_critical",label:"Critical CVE",desc:"New CVSS 9.0+ vulnerability found"},
         {key:"alert_new_device",label:"New Device",desc:"Unrecognized device appears on network"}
        ].map(function(item) {
          return React.createElement("div", { key:item.key, style:{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 12px", background:"#f8fafc", borderRadius:8 } },
            React.createElement("div", null,
              React.createElement("div", { style:{ fontSize:13, fontWeight:500, color:"#0f1923" } }, item.label),
              React.createElement("div", { style:{ fontSize:11, color:"#8a9ab0" } }, item.desc)
            ),
            Toggle(settings[item.key], function(){ update(item.key, !settings[item.key]); })
          );
        })
      )
    ),
    React.createElement("div", { style:card },
      React.createElement("div", { style:{ fontSize:14, fontWeight:600, color:"#0f1923", marginBottom:4 } }, "Report Digest"),
      React.createElement("div", { style:{ fontSize:12, color:"#8a9ab0", marginBottom:16 } }, "Schedule regular security briefing emails"),
      React.createElement("div", { style:{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14, marginBottom:14 } },
        React.createElement("div", null, React.createElement("label", { style:lbl }, "Cadence"),
          React.createElement("select", { style:inp, value:settings.digest_cadence, onChange:function(e){update("digest_cadence",e.target.value);} },
            ["daily","weekly","biweekly","monthly"].map(function(d){ return React.createElement("option",{key:d,value:d},d.charAt(0).toUpperCase()+d.slice(1)); })
          )
        ),
        React.createElement("div", null, React.createElement("label", { style:lbl }, "Day"),
          React.createElement("select", { style:inp, value:settings.digest_day, onChange:function(e){update("digest_day",e.target.value);} },
            ["monday","tuesday","wednesday","thursday","friday"].map(function(d){ return React.createElement("option",{key:d,value:d},d.charAt(0).toUpperCase()+d.slice(1)); })
          )
        ),
        React.createElement("div", null, React.createElement("label", { style:lbl }, "Time"), React.createElement("input", { type:"time", style:inp, value:settings.digest_time, onChange:function(e){update("digest_time",e.target.value);} }))
      ),
      React.createElement("div", { style:{ display:"flex", alignItems:"center", gap:10 } },
        Toggle(settings.attach_pdf, function(){ update("attach_pdf",!settings.attach_pdf); }),
        React.createElement("span", { style:{ fontSize:13, color:"#374151" } }, "Attach PDF report to digest email")
      )
    ),
    React.createElement("div", { style:card },
      React.createElement("div", { style:{ fontSize:14, fontWeight:600, color:"#0f1923", marginBottom:4 } }, "DNS & Network Configuration"),
      React.createElement("div", { style:{ fontSize:12, color:"#8a9ab0", marginBottom:16 } }, "Configure hostname resolution for discovered devices"),
      React.createElement("select", { style:Object.assign({},inp,{maxWidth:300,marginBottom:12}) },
        React.createElement("option", { value:"auto" }, "Auto (mDNS / NetBIOS)"),
        React.createElement("option", { value:"internal" }, "Internal DNS Server"),
        React.createElement("option", { value:"ad" }, "Active Directory / LDAP")
      ),
      React.createElement("div", { style:{ background:"#fffbeb", border:"1px solid #fde68a", borderRadius:8, padding:"10px 14px", fontSize:12, color:"#78350f" } },
        "Active Directory integration unlocks hostname resolution, username mapping, and department visibility."
      )
    ),
    React.createElement("div", { style:{ display:"flex", justifyContent:"flex-end" } },
      React.createElement("button", { onClick:save, disabled:saving, style:{ padding:"10px 24px", borderRadius:8, border:"none", background:"#2b7cd3", color:"#fff", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"inherit" } },
        saving ? "Saving..." : "Save Settings"
      )
    )
  );
}

function SchedulePage({ token }) {
  var ss = React.useState({ discovery_interval:"15", port_scan_interval:"6", cve_scan_interval:"6", kev_sync_interval:"24", shadow_ai_interval:"4", full_scan_time:"02:00", report_day:"monday", report_time:"06:00" });
  var schedule = ss[0]; var setSchedule = ss[1];
  var ms = React.useState(""); var message = ms[0]; var setMessage = ms[1];
  function update(key, val) { setSchedule(function(p){ var n=Object.assign({},p); n[key]=val; return n; }); }
  function save() { setMessage("Schedule saved. Changes take effect at next scan cycle."); setTimeout(function(){ setMessage(""); },4000); }
  var card = { background:"#fff", border:"1px solid #e2e8f0", borderRadius:12, padding:"20px 24px", marginBottom:16, boxShadow:"0 1px 3px rgba(0,0,0,.04)" };
  var lbl = { fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:5 };
  var inp = { width:"100%", padding:"8px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" };
  var scans = [
    {key:"discovery_interval",label:"Device Discovery",unit:"min",desc:"Scan for new network devices",icon:"radar",color:"#2b7cd3"},
    {key:"port_scan_interval",label:"Port Scan",unit:"hrs",desc:"Scan open ports on devices",icon:"network",color:"#c2410c"},
    {key:"cve_scan_interval",label:"CVE Correlation",unit:"hrs",desc:"Check for new vulnerabilities",icon:"bug",color:"#b91c1c"},
    {key:"kev_sync_interval",label:"CISA KEV Sync",unit:"hrs",desc:"Sync active exploit catalog",icon:"flame",color:"#7c3aed"},
    {key:"shadow_ai_interval",label:"Shadow AI Scan",unit:"hrs",desc:"Detect unauthorized AI tool usage",icon:"robot",color:"#a16207"},
  ];
  return React.createElement("div", { style:{ maxWidth:720 } },
    React.createElement("div", { style:{ marginBottom:20 } },
      React.createElement("div", { style:{ fontSize:16, fontWeight:600, color:"#0f1923", marginBottom:4 } }, "Scan Schedule"),
      React.createElement("div", { style:{ fontSize:12, color:"#8a9ab0" } }, "Configure how often each scan type runs")
    ),
    message && React.createElement("div", { style:{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, padding:"10px 14px", marginBottom:16, fontSize:12, color:"#15803d", fontWeight:500 } }, message),
    React.createElement("div", { style:card },
      React.createElement("div", { style:{ fontSize:14, fontWeight:600, color:"#0f1923", marginBottom:16 } }, "Scan Intervals"),
      React.createElement("div", { style:{ display:"flex", flexDirection:"column", gap:10 } },
        scans.map(function(scan) {
          return React.createElement("div", { key:scan.key, style:{ display:"flex", alignItems:"center", gap:14, padding:"12px 14px", background:"#f8fafc", borderRadius:8 } },
            React.createElement("div", { style:{ width:32, height:32, borderRadius:8, background:scan.color+"15", color:scan.color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, flexShrink:0 } },
              React.createElement("i", { className:"ti ti-"+scan.icon })
            ),
            React.createElement("div", { style:{ flex:1 } },
              React.createElement("div", { style:{ fontSize:13, fontWeight:500, color:"#0f1923" } }, scan.label),
              React.createElement("div", { style:{ fontSize:11, color:"#8a9ab0" } }, scan.desc)
            ),
            React.createElement("div", { style:{ display:"flex", alignItems:"center", gap:8, flexShrink:0 } },
              React.createElement("input", { type:"number", min:"1", max:"168", value:schedule[scan.key], onChange:function(e){ update(scan.key,e.target.value); }, style:{ width:60, padding:"6px 8px", borderRadius:6, border:"1px solid #d1d5db", fontSize:13, textAlign:"center", fontFamily:"inherit", outline:"none" } }),
              React.createElement("span", { style:{ fontSize:12, color:"#8a9ab0", minWidth:30 } }, scan.unit)
            )
          );
        })
      )
    ),
    React.createElement("div", { style:card },
      React.createElement("div", { style:{ fontSize:14, fontWeight:600, color:"#0f1923", marginBottom:16 } }, "Scheduled Events"),
      React.createElement("div", { style:{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14 } },
        React.createElement("div", null, React.createElement("label", { style:lbl }, "Full Scan Time (daily)"), React.createElement("input", { type:"time", style:inp, value:schedule.full_scan_time, onChange:function(e){ update("full_scan_time",e.target.value); } })),
        React.createElement("div", null, React.createElement("label", { style:lbl }, "Report Day"),
          React.createElement("select", { style:inp, value:schedule.report_day, onChange:function(e){ update("report_day",e.target.value); } },
            ["monday","tuesday","wednesday","thursday","friday","saturday","sunday"].map(function(d){ return React.createElement("option",{key:d,value:d},d.charAt(0).toUpperCase()+d.slice(1)); })
          )
        ),
        React.createElement("div", null, React.createElement("label", { style:lbl }, "Report Time"), React.createElement("input", { type:"time", style:inp, value:schedule.report_time, onChange:function(e){ update("report_time",e.target.value); } }))
      )
    ),
    React.createElement("div", { style:{ display:"flex", justifyContent:"flex-end" } },
      React.createElement("button", { onClick:save, style:{ padding:"10px 24px", borderRadius:8, border:"none", background:"#2b7cd3", color:"#fff", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"inherit" } }, "Save Schedule")
    )
  );
}

function Dashboard({ token, username, onLogout }) {
  var ds = useState(null); var data = ds[0]; var setData = ds[1];
  var ts = useState("overview"); var tab = ts[0]; var setTab = ts[1];
  var ls = useState(true); var loading = ls[0]; var setLoading = ls[1];
  var us = useState(null); var lastUpdate = us[0]; var setLastUpdate = us[1];
  var ms = useState(null); var triageVuln = ms[0]; var setTriageVuln = ms[1];
  var fs = useState("all"); var vulnFilter = fs[0]; var setVulnFilter = fs[1];
  var cs = useState(null); var cveDetail = cs[0]; var setCveDetail = cs[1];
  var vs = useState({
    ip_address: true, cve_id: true, cvss_score: true, severity: true,
    tags: true, status: true, first_detected: true, last_detected: true,
    required_action: true, triage: true
  });
  var visibleCols = vs[0]; var setVisibleCols = vs[1];
  var ccs = useState(false); var colChooserOpen = ccs[0]; var setColChooserOpen = ccs[1];

  function toggleCol(col) {
    setVisibleCols(function(prev) {
      var next = Object.assign({}, prev);
      next[col] = !next[col];
      return next;
    });
  }
  var ss = useState({col:"cvss_score",dir:"desc"}); var sortState = ss[0]; var setSort = ss[1];

  function toggleSort(col) {
    setSort(function(prev) {
      return { col: col, dir: prev.col === col && prev.dir === "desc" ? "asc" : "desc" };
    });
  }

  function sortIcon(col) {
    if (sortState.col !== col) return React.createElement("i", { className:"ti ti-arrows-sort", style:{ fontSize:10, color:"#d1d5db", marginLeft:3 } });
    return sortState.dir === "desc"
      ? React.createElement("i", { className:"ti ti-arrow-down", style:{ fontSize:10, color:"#2b7cd3", marginLeft:3 } })
      : React.createElement("i", { className:"ti ti-arrow-up", style:{ fontSize:10, color:"#2b7cd3", marginLeft:3 } });
  }

  function sortedVulns(vulns) {
    var col = sortState.col;
    var dir = sortState.dir;
    return [...vulns].sort(function(a, b) {
      var av = a[col], bv = b[col];
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === "number") return dir === "desc" ? bv - av : av - bv;
      return dir === "desc"
        ? String(bv).localeCompare(String(av))
        : String(av).localeCompare(String(bv));
    });
  }

  const fetchData = useCallback(function() {
    fetch(API + "/api/dashboard", { headers: { "Authorization": "Bearer " + token } })
      .then(r => { if (r.status === 401) { onLogout(); return null; } return r.json(); })
      .then(d => { if (d) { setData(d); setLastUpdate(new Date().toLocaleTimeString()); setLoading(false); } })
      .catch(() => setLoading(false));
  }, [token]);

  useEffect(function() {
    fetchData();
    var iv = setInterval(fetchData, 30000);
    return () => clearInterval(iv);
  }, [fetchData]);

  if (loading) return React.createElement("div", { style: { minHeight: "100vh", background: "#f4f6f9", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 10 } },
    React.createElement("div", { style: { fontSize: 18, fontWeight: 600, color: "#1e3a5f" } }, "Slate Clarity"),
    React.createElement("div", { style: { fontSize: 12, color: "#8a9ab0" } }, "Loading network intelligence...")
  );
  if (!data) return null;

  var summary = data.summary;
  var assets = data.assets || [];
  var vulns = data.top_vulnerabilities || [];
  var ports = data.open_ports || [];
  var history = data.scan_history || [];
  var intel = data.threat_intel || [];

  // Filter vulns
  var filteredVulns = vulns.filter(v => {
    if (vulnFilter === "kev") return v.is_kev;
    if (vulnFilter === "ransomware") return v.kev_ransomware === "Known";
    if (vulnFilter === "in_progress") return v.status === "in_progress";
    if (vulnFilter === "acknowledged") return v.status === "acknowledged";
    return true;
  });

  var chartData = assets.filter(a => a.max_score).map(a => ({
    ip: a.ip_address.split(".").pop(), score: parseFloat(a.max_score),
    label: riskLabel(a.max_score)
  })).sort((a,b) => b.score - a.score).slice(0, 8);

  var BRAND = "#1e3a5f";
  var BRAND_MID = "#254a77";

  var navItems = [
    { id: "overview", icon: "layout-dashboard", label: "Overview" },
    { id: "assets", icon: "device-desktop", label: "Assets", badge: summary.total_assets },
    { id: "vulnerabilities", icon: "shield-exclamation", label: "Vulnerabilities", badge: summary.critical, badgeColor: "#b91c1c" },
    { id: "ports", icon: "network", label: "Ports & Services" },
    { id: "intel", icon: "world", label: "Threat Intelligence" },
    { id: "history", icon: "clock-hour-4", label: "Scan Activity" },
    { id: "shadow", icon: "robot", label: "Shadow AI" },
  ];

  var settingsItems = [
    { id: "detectors", icon: "radar", label: "Detectors" },
    { id: "schedule", icon: "calendar-time", label: "Scan Schedule" },
    { id: "users", icon: "users", label: "Users & Roles" },
    { id: "integrations", icon: "plug", label: "Integrations" },
    { id: "settings", icon: "settings", label: "Settings" },
  ];

  return React.createElement("div", {
    style: { display: "grid", gridTemplateColumns: "240px 1fr", height: "100vh", fontFamily: "'DM Sans', sans-serif", fontSize: 14, background: "#F7F8FA" }
  },

    // Sidebar
    React.createElement("aside", { style: { background: "#fff", borderRight: "1px solid #E8EAED", display: "flex", flexDirection: "column", overflow: "hidden" } },

      // Logo
      React.createElement("div", { style: { padding: "20px 18px 16px" } },
        React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 10 } },
          React.createElement("div", { style: { width: 30, height: 30, borderRadius: 8, background: "#2F5DFF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 } },
            React.createElement("div", { style: { width: 8, height: 8, borderRadius: "50%", background: "#fff" } })
          ),
          React.createElement("div", null,
            React.createElement("div", { style: { fontSize: 14, fontWeight: 700, color: "#15181F", letterSpacing: "-0.01em" } }, "Slate Clarity"),
            React.createElement("div", { style: { fontSize: 9, color: "#9CA3AF", letterSpacing: ".6px", textTransform: "uppercase", marginTop: 1 } }, "Network Intelligence")
          )
        )
      ),

      // Org switcher
      React.createElement("div", {
        style: { margin: "2px 12px 8px", padding: "9px 11px", background: "#F7F8FA", border: "1px solid #E8EAED", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }
      },
        React.createElement("div", null,
          React.createElement("div", { style: { fontSize: 11, fontWeight: 600, color: "#15181F" } }, "My Organization"),
          React.createElement("div", { style: { fontSize: 10, color: "#9CA3AF", marginTop: 1 } }, "Primary \u00b7 18 devices")
        ),
        React.createElement("i", { className: "ti ti-chevron-down", style: { fontSize: 12, color: "#9CA3AF" } })
      ),

      // Nav
      React.createElement("nav", { style: { padding: "8px 12px", flex: 1, overflowY: "auto" } },
        React.createElement("div", { style: { fontSize: 9, fontWeight: 600, color: "#9CA3AF", letterSpacing: ".8px", textTransform: "uppercase", padding: "10px 8px 6px" } }, "Intelligence"),
        navItems.map(item => {
          var active = tab === item.id;
          return React.createElement("div", {
            key: item.id, onClick: () => setTab(item.id),
            style: {
              display: "flex", alignItems: "center", gap: 9,
              padding: "7px 9px", borderRadius: 7, cursor: "pointer",
              color: active ? "#2F5DFF" : "#4B5563",
              background: active ? "#EEF2FF" : "transparent",
              fontWeight: active ? 600 : 500, fontSize: 12.5,
              marginBottom: 1, borderLeft: active ? "3px solid #2F5DFF" : "3px solid transparent",
              transition: "background .12s, color .12s"
            },
            onMouseEnter: e => { if (!active) e.currentTarget.style.background = "#F7F8FA"; },
            onMouseLeave: e => { if (!active) e.currentTarget.style.background = "transparent"; }
          },
            React.createElement("i", { className: "ti ti-" + item.icon, style: { fontSize: 14, width: 15 } }),
            item.label,
            item.badge ? React.createElement("span", {
              style: { marginLeft: "auto", fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 999,
                background: item.badgeColor || "#EEF2FF", color: item.badgeColor ? "#fff" : "#2F5DFF" }
            }, item.badge) : null
          );
        }),

        React.createElement("div", { style: { fontSize: 9, fontWeight: 600, color: "#9CA3AF", letterSpacing: ".8px", textTransform: "uppercase", padding: "16px 8px 6px" } }, "Configuration"),
        settingsItems.map(item => {
          var active = tab === item.id;
          return React.createElement("div", {
            key: item.id, onClick: () => setTab(item.id),
            style: {
              display: "flex", alignItems: "center", gap: 9,
              padding: "7px 9px", borderRadius: 7, cursor: "pointer",
              color: active ? "#2F5DFF" : "#6B7280",
              background: active ? "#EEF2FF" : "transparent",
              fontSize: 12.5, fontWeight: active ? 600 : 500, marginBottom: 1,
              borderLeft: active ? "3px solid #2F5DFF" : "3px solid transparent"
            },
            onMouseEnter: e => { if (!active) e.currentTarget.style.background = "#F7F8FA"; },
            onMouseLeave: e => { if (!active) e.currentTarget.style.background = "transparent"; }
          },
            React.createElement("i", { className: "ti ti-" + item.icon, style: { fontSize: 14, width: 15 } }),
            item.label
          );
        }),

        React.createElement("div", { style: { fontSize: 9, fontWeight: 600, color: "#9CA3AF", letterSpacing: ".8px", textTransform: "uppercase", padding: "16px 8px 6px" } }, "MSSP Portal"),
        React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 9, padding: "7px 9px", borderRadius: 7, cursor: "pointer", color: "#6B7280", fontSize: 12.5, fontWeight: 500, marginBottom: 1 } },
          React.createElement("i", { className: "ti ti-building", style: { fontSize: 14, width: 15 } }), "All Clients",
          React.createElement("span", { style: { marginLeft: "auto", fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 999, background: "#FFF1E8", color: "#C2410C" } }, "3")
        ),
        React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 9, padding: "7px 9px", borderRadius: 7, cursor: "pointer", color: "#6B7280", fontSize: 12.5, fontWeight: 500 } },
          React.createElement("i", { className: "ti ti-chart-bar", style: { fontSize: 14, width: 15 } }), "Benchmarking"
        )
      ),

      // Footer
      React.createElement("div", { style: { padding: "12px", borderTop: "1px solid #E8EAED" } },
        React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 9, padding: "8px 9px", borderRadius: 8, cursor: "pointer" },
          onMouseEnter: e => e.currentTarget.style.background = "#F7F8FA",
          onMouseLeave: e => e.currentTarget.style.background = "transparent"
        },
          React.createElement("div", { style: { width: 28, height: 28, borderRadius: 7, background: "#2F5DFF", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "#fff", flexShrink: 0 } }, "SL"),
          React.createElement("div", { style: { minWidth: 0 } },
            React.createElement("div", { style: { fontSize: 11, fontWeight: 600, color: "#15181F" } }, username || "Admin"),
            React.createElement("div", { style: { fontSize: 9.5, color: "#9CA3AF" } }, "admin@slategroup.com")
          ),
          React.createElement("span", { style: { marginLeft: "auto", fontSize: 9, fontWeight: 700, background: "#ECFDF5", color: "#16A34A", border: "1px solid #BBF7D0", padding: "1px 5px", borderRadius: 4 } }, "MFA"),
          React.createElement("button", { onClick: onLogout, style: { background: "none", border: "none", color: "#9CA3AF", cursor: "pointer", fontSize: 13, marginLeft: 4 } },
            React.createElement("i", { className: "ti ti-logout" })
          )
        )
      )
    ),

    // Main content
    React.createElement("main", { style: { display: "flex", flexDirection: "column", overflow: "hidden" } },

      // Topbar
      React.createElement("div", { style: { height: 60, background: "#fff", borderBottom: "1px solid #E8EAED", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 28px", flexShrink: 0 } },
        React.createElement("div", null,
          React.createElement("div", { style: { fontSize: 17, fontWeight: 700, color: "#15181F", letterSpacing: "-0.01em", textTransform: "capitalize" } }, tab.replace("_", " ")),
          React.createElement("div", { style: { fontSize: 11.5, color: "#9CA3AF", marginTop: 1 } }, "Slate Clarity \u00b7 Updated " + (lastUpdate || "\u2014"))
        ),
        React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 10 } },
          React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 999, background: "#ECFDF5", border: "1px solid #BBF7D0", fontSize: 11, fontWeight: 600, color: "#16A34A" } },
            React.createElement("div", { style: { width: 6, height: 6, borderRadius: "50%", background: "#16A34A" } }),
            "Scanning \u00b7 Auto-refresh 30s"
          ),
          React.createElement("button", {
            onClick: function() {
              fetch(API + "/api/scan/trigger", {
                method: "POST",
                headers: { "Authorization": "Bearer " + token }
              })
              .then(r => r.json())
              .then(d => alert(d.message || "Scan triggered"))
              .catch(() => alert("Failed to trigger scan"));
            },
            style: { display: "flex", alignItems: "center", gap: 5, padding: "7px 14px", borderRadius: 8, border: "1px solid #E8EAED", background: "#fff", color: "#4B5563", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }
          },
            React.createElement("i", { className: "ti ti-refresh", style: { fontSize: 13 } }), "Run Scan"
          ),
          React.createElement("button", { style: { display: "flex", alignItems: "center", gap: 5, padding: "7px 14px", borderRadius: 8, border: "none", background: "#2F5DFF", color: "#fff", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" } },
            React.createElement("i", { className: "ti ti-file-download", style: { fontSize: 13 } }), "Export Report"
          )
        )
      ),

      // Content
      React.createElement("div", { style: { flex: 1, overflowY: "auto", padding: "20px 24px" } },

        // ── OVERVIEW TAB ──
        tab === "overview" && React.createElement("div", null,

          // Risk banner
          React.createElement("div", { style: { background: "#fff", border: "1px solid #fecaca", borderLeft: "4px solid #b91c1c", borderRadius: 12, padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, boxShadow: "0 1px 3px rgba(0,0,0,.04)" } },
            React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 14 } },
              React.createElement("div", { style: { width: 36, height: 36, borderRadius: 8, background: "#fef2f2", border: "1px solid #fecaca", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, color: "#b91c1c" } },
                React.createElement("i", { className: "ti ti-alert-triangle" })
              ),
              React.createElement("div", null,
                React.createElement("div", { style: { fontSize: 10, fontWeight: 500, color: "#b91c1c", textTransform: "uppercase", letterSpacing: ".4px" } }, "Overall Network Risk"),
                React.createElement("div", { style: { fontSize: 20, fontWeight: 600, color: "#b91c1c", lineHeight: 1.2 } }, summary.overall_risk)
              )
            ),
            React.createElement("div", { style: { display: "flex", gap: 28 } },
              [
                ["Critical CVEs", summary.critical],
                ["KEV Matches", summary.kev_total || 0],
                ["In Progress", summary.in_progress || 0],
                ["Suppressed", summary.suppressed || 0],
              ].map(([label, val]) =>
                React.createElement("div", { key: label, style: { textAlign: "right" } },
                  React.createElement("div", { style: { fontSize: 10, color: "#8a9ab0", textTransform: "uppercase", letterSpacing: ".3px" } }, label),
                  React.createElement("div", { style: { fontSize: 16, fontWeight: 600, color: "#0f1923", fontFamily: "monospace" } }, val)
                )
              )
            )
          ),

          // Stats
          React.createElement("div", { style: { display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 10, marginBottom: 16 } },
            React.createElement(StatCard, { label: "Assets", value: summary.total_assets, icon: "device-desktop", colorClass: "blue", trend: "Stable", trendDir: "flat", onClick: () => setTab("assets") }),
            React.createElement(StatCard, { label: "Vulnerabilities", value: summary.total_vulns, icon: "bug", colorClass: "orange", trend: "+104", trendDir: "up", onClick: () => setTab("vulnerabilities") }),
            React.createElement(StatCard, { label: "Critical", value: summary.critical, icon: "shield-x", colorClass: "red", trend: "Act now", trendDir: "up", onClick: () => setTab("vulnerabilities") }),
            React.createElement(StatCard, { label: "High Severity", value: summary.high, icon: "alert-circle", colorClass: "yellow", trend: "Stable", trendDir: "flat", onClick: () => setTab("vulnerabilities") }),
            React.createElement(StatCard, { label: "Shadow AI Tools", value: summary.shadow_ai_tools || 0, icon: "robot", colorClass: summary.shadow_ai_tools > 0 ? "orange" : "gray", trend: summary.shadow_ai_tools > 0 ? "Review" : "None", trendDir: summary.shadow_ai_tools > 0 ? "up" : "flat", onClick: () => setTab("shadow") }),
            React.createElement(StatCard, { label: "Active Exploits", value: summary.kev_total || 0, icon: "flame", colorClass: summary.kev_total > 0 ? "red" : "green", trend: summary.kev_total > 0 ? "URGENT" : "Clear", trendDir: summary.kev_total > 0 ? "up" : "good", onClick: () => { setTab("vulnerabilities"); setVulnFilter("kev"); } })
          ),

          // Two column
          React.createElement("div", { style: { display: "grid", gridTemplateColumns: "1fr 340px", gap: 14, marginBottom: 14 } },

            // Vuln table
            React.createElement("div", { style: { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,.04)" } },
              React.createElement("div", { style: { padding: "14px 18px 0", display: "flex", alignItems: "center", justifyContent: "space-between" } },
                React.createElement("div", { style: { fontSize: 13, fontWeight: 600, color: "#0f1923" } }, "Top Vulnerabilities"),
                React.createElement("div", { style: { display: "flex", gap: 4 } },
                  ["all","kev","ransomware","in_progress","acknowledged"].map(f =>
                    React.createElement("button", {
                      key: f, onClick: () => setVulnFilter(f),
                      style: { padding: "3px 8px", borderRadius: 5, border: "1px solid", fontSize: 10, cursor: "pointer", fontFamily: "inherit", fontWeight: 500,
                        background: vulnFilter === f ? "#2b7cd3" : "#fff",
                        color: vulnFilter === f ? "#fff" : "#6b7280",
                        borderColor: vulnFilter === f ? "#2b7cd3" : "#e2e8f0" }
                    }, f === "all" ? "All" : f === "kev" ? "KEV" : f === "ransomware" ? "Ransomware" : f === "in_progress" ? "In Progress" : "Acknowledged")
                  )
                )
              ),
              React.createElement("div", { style: { padding: "12px 18px 16px", overflowX: "auto" } },
                React.createElement("table", { style: { width: "100%", borderCollapse: "collapse" } },
                  React.createElement("thead", null,
                    React.createElement("tr", null,
                      ["IP", "CVE ID", "CVSS", "Tags", "Status", "Action"].map(h =>
                        React.createElement("th", {
                      key: h.label,
                      onClick: h.col ? () => toggleSort(h.col) : undefined,
                      style: {
                        textAlign:"left", fontSize:10, fontWeight:600, color:"#8a9ab0",
                        textTransform:"uppercase", letterSpacing:".4px",
                        padding:"0 8px 8px", borderBottom:"1px solid #f1f5f9",
                        cursor: h.col ? "pointer" : "default", userSelect:"none"
                      }
                    }, h.label, h.col && sortIcon(h.col))
                      )
                    )
                  ),
                  React.createElement("tbody", null,
                    filteredVulns.slice(0, 10).map((v, i) =>
                      React.createElement("tr", { key: i, style: { borderBottom: "1px solid #f8fafc" },
                        onMouseEnter: e => e.currentTarget.style.background = "#f8fafc",
                        onMouseLeave: e => e.currentTarget.style.background = ""
                      },
                        React.createElement("td", { style: { padding: "8px", fontFamily: "monospace", fontSize: 11, color: "#0f1923", fontWeight: 500 } }, v.ip_address),
                        React.createElement("td", { style: { padding: "8px" } },
                    React.createElement("span", {
                      onClick: () => setCveDetail(v),
                      style: { fontFamily:"monospace", fontSize:11, color:"#2b7cd3", cursor:"pointer", textDecoration:"underline", textDecorationStyle:"dotted" }
                    }, v.cve_id)
                  ),
                        visibleCols["cvss_score"] !== false && React.createElement("td", { style: { padding: "8px", fontFamily: "monospace", fontSize: 11, fontWeight: 600, color: SEVERITY_COLOR[riskLabel(v.cvss_score)] } }, v.cvss_score),
                        React.createElement("td", { style: { padding: "8px" } },
                          React.createElement("div", { style: { display: "flex", gap: 3, flexWrap: "wrap" } },
                            v.is_kev && React.createElement(KEVBadge),
                            v.kev_ransomware === "Known" && React.createElement(RansomwareBadge),
                            !v.is_kev && !v.kev_ransomware && React.createElement(SeverityBadge, { label: v.severity })
                          )
                        ),
                        React.createElement("td", { style: { padding: "8px" } },
                          React.createElement(StatusBadge, { status: v.status || "unreviewed" })
                        ),
                        React.createElement("td", { style: { padding: "8px" } },
                          React.createElement("button", {
                            onClick: () => setTriageVuln(v),
                            style: { padding: "3px 9px", borderRadius: 5, border: "1px solid #e2e8f0", background: "#fff", color: "#2b7cd3", fontSize: 10, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" }
                          }, "Triage")
                        )
                      )
                    )
                  )
                )
              )
            ),

            // Asset risk
            React.createElement("div", { style: { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,.04)" } },
              React.createElement("div", { style: { padding: "14px 18px 0", display: "flex", alignItems: "center", justifyContent: "space-between" } },
                React.createElement("div", { style: { fontSize: 13, fontWeight: 600 } }, "Asset Risk"),
                React.createElement("div", { style: { fontSize: 11, color: "#2b7cd3", cursor: "pointer" }, onClick: () => setTab("assets") }, "View all →")
              ),
              React.createElement("div", { style: { padding: "12px 16px 16px" } },
                React.createElement("div", { style: { marginBottom: 12 } },
                  React.createElement("div", { style: { fontSize: 10, color: "#8a9ab0", textTransform: "uppercase", letterSpacing: ".4px", marginBottom: 6 } }, "Risk by device"),
                  React.createElement(ResponsiveContainer, { width: "100%", height: 70 },
                    React.createElement(BarChart, { data: chartData, margin: { top: 0, right: 0, bottom: 0, left: -20 } },
                      React.createElement(XAxis, { dataKey: "ip", tick: { fontSize: 9 }, axisLine: false, tickLine: false }),
                      React.createElement(YAxis, { domain: [0,10], tick: { fontSize: 9 }, axisLine: false, tickLine: false }),
                      React.createElement(Tooltip, { formatter: v => ["CVSS " + v, "Score"], contentStyle: { fontSize: 11, borderRadius: 6 } }),
                      React.createElement(Bar, { dataKey: "score", radius: [3,3,0,0] },
                        chartData.map((d,i) => React.createElement(Cell, { key: i, fill: SEVERITY_COLOR[d.label] || "#e2e8f0" }))
                      )
                    )
                  )
                ),
                assets.slice(0, 5).map((a, i) =>
                  React.createElement("div", {
                    key: i,
                    style: { display: "flex", alignItems: "center", gap: 9, padding: "7px 8px", borderRadius: 7, cursor: "pointer", marginBottom: 2 },
                    onMouseEnter: e => e.currentTarget.style.background = "#f8fafc",
                    onMouseLeave: e => e.currentTarget.style.background = ""
                  },
                    React.createElement("div", { style: { width: 26, height: 26, borderRadius: 6, background: (a.device_color || "#999") + "15", color: a.device_color || "#999", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, flexShrink: 0 } },
                      React.createElement("i", { className: "ti ti-" + (a.device_icon || "device-desktop") })
                    ),
                    React.createElement("div", { style: { flex: 1, minWidth: 0 } },
                      React.createElement("div", { style: { fontFamily: "monospace", fontSize: 11, fontWeight: 500, color: "#0f1923" } }, a.ip_address),
                      React.createElement("div", { style: { fontSize: 10, color: "#8a9ab0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } }, a.device_type + " · " + a.vendor)
                    ),
                    a.max_score && React.createElement("span", {
                      style: { fontFamily: "monospace", fontSize: 11, fontWeight: 600, padding: "2px 7px", borderRadius: 4,
                        background: SEVERITY_BG[riskLabel(a.max_score)], color: SEVERITY_COLOR[riskLabel(a.max_score)] }
                    }, a.max_score)
                  )
                )
              )
            )
          ),

          // Bottom row
          React.createElement("div", { style: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 } },

            // Ports
            React.createElement("div", { style: { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,.04)" } },
              React.createElement("div", { style: { padding: "14px 18px 0", display: "flex", alignItems: "center", justifyContent: "space-between" } },
                React.createElement("div", { style: { fontSize: 13, fontWeight: 600 } }, "Open Ports"),
                React.createElement("div", { style: { fontSize: 11, color: "#2b7cd3", cursor: "pointer" }, onClick: () => setTab("ports") }, "All " + ports.length + " →")
              ),
              React.createElement("div", { style: { padding: "10px 18px 16px" } },
                ports.slice(0, 6).map((p, i) =>
                  React.createElement("div", { key: i, style: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 0", borderBottom: i < 5 ? "1px solid #f8fafc" : "none" } },
                    React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 10 } },
                      React.createElement("div", { style: { fontFamily: "monospace", fontSize: 11, fontWeight: 500, color: "#0f1923", width: 34 } }, p.port),
                      React.createElement("div", null,
                        React.createElement("div", { style: { fontSize: 12, color: "#0f1923" } }, p.service || "—"),
                        React.createElement("div", { style: { fontSize: 10, color: "#8a9ab0" } }, p.ip_address)
                      )
                    ),
                    p.port === 23 ? React.createElement("span", { style: { fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 4, background: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca" } }, "⚠ Insecure") :
                    p.port === 445 ? React.createElement("span", { style: { fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 4, background: "#fff7ed", color: "#c2410c", border: "1px solid #fed7aa" } }, "Review") :
                    p.port === 5900 ? React.createElement("span", { style: { fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 4, background: "#fff7ed", color: "#c2410c", border: "1px solid #fed7aa" } }, "VNC") :
                    React.createElement("span", { style: { fontSize: 10, color: "#8a9ab0" } }, "Normal")
                  )
                )
              )
            ),

            // Scan Activity (combined)
            React.createElement("div", { style: { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,.04)" } },
              React.createElement("div", { style: { padding: "14px 18px 0", display: "flex", alignItems: "center", justifyContent: "space-between" } },
                React.createElement("div", { style: { fontSize: 13, fontWeight: 600 } }, "Scan Activity"),
                React.createElement("div", { style: { fontSize: 11, color: "#2b7cd3", cursor: "pointer" }, onClick: () => setTab("history") }, "Full history →")
              ),
              React.createElement("div", { style: { padding: "10px 18px 16px" } },
                [
                  { type: "Discovery", freq: "Every 15 min", icon: "radar", color: "#2b7cd3" },
                  { type: "Port Scan", freq: "Every 6 hrs", icon: "network", color: "#c2410c" },
                  { type: "CVE Scan", freq: "Every 6 hrs", icon: "bug", color: "#b91c1c" },
                  { type: "KEV Sync", freq: "Every 24 hrs", icon: "flame", color: "#7c3aed" },
                  { type: "Report", freq: "Monday 6am", icon: "file-analytics", color: "#a16207" },
                ].map((item, i) => {
                  var last = history.find(h => h.scan_type && h.scan_type.toLowerCase().includes(item.type.toLowerCase().split(" ")[0]));
                  return React.createElement("div", { key: i, style: { display: "flex", alignItems: "center", gap: 9, padding: "6px 0", borderBottom: i < 4 ? "1px solid #f8fafc" : "none" } },
                    React.createElement("div", { style: { width: 24, height: 24, borderRadius: 5, background: item.color + "15", color: item.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, flexShrink: 0 } },
                      React.createElement("i", { className: "ti ti-" + item.icon })
                    ),
                    React.createElement("div", { style: { flex: 1 } },
                      React.createElement("div", { style: { fontSize: 12, fontWeight: 500, color: "#0f1923" } }, item.type),
                      React.createElement("div", { style: { fontSize: 10, color: "#8a9ab0" } }, item.freq)
                    ),
                    React.createElement("div", { style: { textAlign: "right" } },
                      React.createElement("div", { style: { fontSize: 10, color: "#15803d", fontWeight: 500 } }, "● Active"),
                      last && React.createElement("div", { style: { fontSize: 9, color: "#8a9ab0" } },
                        last.assets_found ? last.assets_found + " assets" : last.vulnerabilities_found ? last.vulnerabilities_found + " CVEs" : "—"
                      )
                    )
                  );
                })
              )
            ),

            // Threat Intel preview
            React.createElement("div", { style: { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,.04)" } },
              React.createElement("div", { style: { padding: "14px 18px 0", display: "flex", alignItems: "center", justifyContent: "space-between" } },
                React.createElement("div", { style: { fontSize: 13, fontWeight: 600 } }, "Threat Intelligence"),
                React.createElement("div", { style: { fontSize: 11, color: "#2b7cd3", cursor: "pointer" }, onClick: () => setTab("intel") }, "View all →")
              ),
              React.createElement("div", { style: { padding: "10px 18px 16px" } },
                intel.slice(0, 4).map((item, i) =>
                  React.createElement("div", { key: i,
                    onClick: () => item.url && window.open(item.url, "_blank"),
                    style: { padding: "8px 0", borderBottom: i < 3 ? "1px solid #f8fafc" : "none", cursor: item.url ? "pointer" : "default" }
                  },
                    React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 6, marginBottom: 2 } },
                      React.createElement("span", { style: { fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 3,
                        background: item.severity === "critical" ? "#fef2f2" : item.severity === "high" ? "#fff7ed" : "#f0f9ff",
                        color: item.severity === "critical" ? "#b91c1c" : item.severity === "high" ? "#c2410c" : "#0369a1",
                        border: "1px solid " + (item.severity === "critical" ? "#fecaca" : item.severity === "high" ? "#fed7aa" : "#bae6fd") } }, item.source),
                      item.url && React.createElement("i", { className: "ti ti-external-link", style: { fontSize: 10, color: "#8a9ab0" } })
                    ),
                    React.createElement("div", { style: { fontSize: 11, fontWeight: 500, color: "#0f1923", lineHeight: 1.3 } }, item.title.slice(0, 55) + (item.title.length > 55 ? "..." : ""))
                  )
                )
              )
            )
          )
        ),

        // ── ASSETS TAB ──
        tab === "assets" && React.createElement("div", { style: { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,.04)" } },
          React.createElement("div", { style: { padding: "14px 18px 0" } },
            React.createElement("div", { style: { fontSize: 13, fontWeight: 600 } }, "Network Asset Inventory — " + assets.length + " devices")
          ),
          React.createElement("div", { style: { padding: "12px 18px 18px", overflowX: "auto" } },
            React.createElement("table", { style: { width: "100%", borderCollapse: "collapse" } },
              React.createElement("thead", null,
                React.createElement("tr", null,
                  [
  {label:"IP Address",     col:"ip_address"},
  {label:"Device",         col:"device_type"},
  {label:"MAC Address",    col:"mac_address"},
  {label:"OS / Fingerprint", col:"os_guess"},
  {label:"CVEs",           col:"vuln_count"},
  {label:"KEV",            col:"kev_count"},
  {label:"Max CVSS",       col:"max_score"},
  {label:"Risk",           col:"max_score"},
  {label:"Last Seen",      col:"last_seen"},
].map(h =>
                    React.createElement("th", {
                      key: h.label,
                      onClick: h.col ? () => toggleSort(h.col) : undefined,
                      style: {
                        textAlign:"left", fontSize:10, fontWeight:600, color:"#8a9ab0",
                        textTransform:"uppercase", letterSpacing:".4px",
                        padding:"0 8px 8px", borderBottom:"1px solid #f1f5f9",
                        whiteSpace:"nowrap",
                        cursor: h.col ? "pointer" : "default",
                        userSelect:"none"
                      }
                    }, h.label, h.col && sortIcon(h.col))
                  )
                )
              ),
              React.createElement("tbody", null,
                [...assets].sort(function(a,b){ var col=sortState.col,dir=sortState.dir,av=a[col],bv=b[col]; if(!av)return 1; if(!bv)return -1; if(typeof av==="number")return dir==="desc"?bv-av:av-bv; return dir==="desc"?String(bv).localeCompare(String(av)):String(av).localeCompare(String(bv)); }).map((a, i) => {
                  var label = riskLabel(a.max_score);
                  return React.createElement("tr", { key: i, style: { borderBottom: "1px solid #f8fafc" },
                    onMouseEnter: e => e.currentTarget.style.background = "#f8fafc",
                    onMouseLeave: e => e.currentTarget.style.background = ""
                  },
                    React.createElement("td", { style: { padding: "8px", fontFamily: "monospace", fontSize: 11, fontWeight: 500, color: "#0f1923" } }, a.ip_address),
                    React.createElement("td", { style: { padding: "8px" } },
                      React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 7 } },
                        React.createElement("div", { style: { width: 26, height: 26, borderRadius: 6, background: (a.device_color||"#999")+"15", color: a.device_color||"#999", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13 } },
                          React.createElement("i", { className: "ti ti-" + (a.device_icon||"device-desktop") })
                        ),
                        React.createElement("div", null,
                          React.createElement("div", { style: { fontSize: 11, fontWeight: 500, color: "#0f1923" } }, a.device_type || "Unknown"),
                          React.createElement("div", { style: { fontSize: 10, color: "#8a9ab0" } }, a.vendor || "")
                        )
                      )
                    ),
                    React.createElement("td", { style: { padding: "8px", fontFamily: "monospace", fontSize: 10, color: "#8a9ab0" } }, a.mac_address || "—"),
                    React.createElement("td", { style: { padding: "8px", fontSize: 11, color: "#4a5568", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } }, (a.os_guess || "Unknown").slice(0,45)),
                    React.createElement("td", { style: { padding: "8px", textAlign: "center", fontSize: 12, fontWeight: 500 } }, a.vuln_count || 0),
                    React.createElement("td", { style: { padding: "8px", textAlign: "center" } },
                      a.kev_count > 0 ? React.createElement(KEVBadge) : React.createElement("span", { style: { color: "#8a9ab0", fontSize: 11 } }, "—")
                    ),
                    React.createElement("td", { style: { padding: "8px", textAlign: "center", fontFamily: "monospace", fontSize: 11, fontWeight: 600, color: SEVERITY_COLOR[label] } }, a.max_score || "—"),
                    React.createElement("td", { style: { padding: "8px" } }, React.createElement(SeverityBadge, { label: label })),
                    React.createElement("td", { style: { padding: "8px", fontSize: 10, color: "#8a9ab0" } }, a.last_seen ? new Date(a.last_seen).toLocaleDateString() : "—")
                  );
                })
              )
            )
          )
        ),

        // ── VULNERABILITIES TAB ──
        tab === "vulnerabilities" && React.createElement("div", { style: { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,.04)" } },
          React.createElement("div", { style: { padding: "14px 18px 0", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap:"wrap", gap:8 } },
            React.createElement("div", { style: { fontSize: 13, fontWeight: 600 } }, "Vulnerabilities — " + summary.total_vulns + " active · " + (summary.suppressed||0) + " suppressed"),
            React.createElement("div", { style: { display: "flex", alignItems:"center", gap: 8 } },
              React.createElement("div", { style:{ position:"relative" } },
                React.createElement("button", {
                  onClick: () => setColChooserOpen(!colChooserOpen),
                  style:{ display:"flex", alignItems:"center", gap:4, padding:"4px 10px", borderRadius:6, border:"1px solid #e2e8f0", background: colChooserOpen ? "#f1f5f9" : "#fff", color:"#6b7280", fontSize:11, cursor:"pointer", fontFamily:"inherit" }
                },
                  React.createElement("i", { className:"ti ti-columns", style:{ fontSize:12 } }), " Columns"
                ),
                colChooserOpen && React.createElement("div", {
                  style:{ position:"absolute", right:0, top:"calc(100% + 4px)", background:"#fff", border:"1px solid #e2e8f0", borderRadius:8, boxShadow:"0 4px 12px rgba(0,0,0,.08)", zIndex:200, padding:"8px 0", minWidth:180 }
                },
                  React.createElement("div", { style:{ padding:"4px 12px 8px", fontSize:10, fontWeight:600, color:"#8a9ab0", textTransform:"uppercase", letterSpacing:".4px", borderBottom:"1px solid #f1f5f9", marginBottom:4 } }, "Show / Hide Columns"),
                  [
                    {key:"ip_address",     label:"IP Address"},
                    {key:"cve_id",         label:"CVE ID"},
                    {key:"cvss_score",     label:"CVSS Score"},
                    {key:"severity",       label:"Severity"},
                    {key:"tags",           label:"Tags"},
                    {key:"status",         label:"Status"},
                    {key:"first_detected", label:"First Seen"},
                    {key:"last_detected",  label:"Last Seen"},
                    {key:"required_action",label:"Required Action"},
                    {key:"triage",         label:"Triage"},
                  ].map(function(c) {
                    return React.createElement("div", {
                      key: c.key,
                      onClick: () => toggleCol(c.key),
                      style:{ display:"flex", alignItems:"center", gap:8, padding:"6px 12px", cursor:"pointer", fontSize:12, color:"#374151" },
                      onMouseEnter: e => e.currentTarget.style.background = "#f8fafc",
                      onMouseLeave: e => e.currentTarget.style.background = ""
                    },
                      React.createElement("div", {
                        style:{ width:14, height:14, borderRadius:3, border:"1px solid", flexShrink:0,
                          background: visibleCols[c.key] !== false ? "#2b7cd3" : "#fff",
                          borderColor: visibleCols[c.key] !== false ? "#2b7cd3" : "#d1d5db",
                          display:"flex", alignItems:"center", justifyContent:"center"
                        }
                      },
                        visibleCols[c.key] !== false && React.createElement("i", { className:"ti ti-check", style:{ fontSize:9, color:"#fff" } })
                      ),
                      c.label
                    );
                  })
                )
              ),
              React.createElement("div", { style: { display: "flex", gap: 4 } },
                ["all","kev","ransomware","in_progress","acknowledged"].map(f =>
                  React.createElement("button", { key: f, onClick: () => setVulnFilter(f),
                    style: { padding: "4px 9px", borderRadius: 5, border: "1px solid", fontSize: 10, cursor: "pointer", fontFamily: "inherit", fontWeight: 500,
                      background: vulnFilter === f ? "#2b7cd3" : "#fff",
                      color: vulnFilter === f ? "#fff" : "#6b7280",
                      borderColor: vulnFilter === f ? "#2b7cd3" : "#e2e8f0" }
                  }, f === "all" ? "All" : f === "kev" ? "⚡ KEV" : f === "ransomware" ? "🔴 Ransomware" : f === "in_progress" ? "In Progress" : "Acknowledged")
                )
              )
            )
          ),
          React.createElement("div", { style: { padding: "12px 18px 18px", overflowX: "auto" } },
            React.createElement("table", { style: { width: "100%", borderCollapse: "collapse" } },
              React.createElement("thead", null,
                React.createElement("tr", null,
                  [
                    {label:"IP Address",     col:"ip_address",     key:"ip_address"},
                    {label:"CVE ID",         col:"cve_id",         key:"cve_id"},
                    {label:"CVSS",           col:"cvss_score",     key:"cvss_score"},
                    {label:"Severity",       col:"severity",       key:"severity"},
                    {label:"Tags",           col:null,             key:"tags"},
                    {label:"Status",         col:"status",         key:"status"},
                    {label:"First Seen",     col:"first_detected", key:"first_detected"},
                    {label:"Last Seen",      col:"last_detected",  key:"last_detected"},
                    {label:"Required Action",col:null,             key:"required_action"},
                    {label:"Triage",         col:null,             key:"triage"},
                  ].filter(h => visibleCols[h.key] !== false).map(h =>
                    React.createElement("th", {
                      key: h.label,
                      onClick: h.col ? () => toggleSort(h.col) : undefined,
                      style: {
                        textAlign:"left", fontSize:10, fontWeight:600, color:"#8a9ab0",
                        textTransform:"uppercase", letterSpacing:".4px",
                        padding:"0 8px 8px", borderBottom:"1px solid #f1f5f9",
                        whiteSpace:"nowrap",
                        cursor: h.col ? "pointer" : "default",
                        userSelect:"none"
                      }
                    }, h.label, h.col && sortIcon(h.col))
                  )
                )
              ),
              React.createElement("tbody", null,
                sortedVulns(filteredVulns).map((v, i) =>
                  React.createElement("tr", { key: i, style: { borderBottom: "1px solid #f8fafc" },
                    onMouseEnter: e => e.currentTarget.style.background = "#f8fafc",
                    onMouseLeave: e => e.currentTarget.style.background = ""
                  },
                    visibleCols["ip_address"] !== false && React.createElement("td", { style: { padding: "8px", fontFamily: "monospace", fontSize: 11, color: "#0f1923" } }, v.ip_address),
                    visibleCols["cve_id"] !== false && React.createElement("td", { style: { padding: "8px" } },
                      React.createElement("span", {
                        onClick: () => setCveDetail(v),
                        style: { fontFamily:"monospace", fontSize:11, color:"#2b7cd3", cursor:"pointer", textDecoration:"underline", textDecorationStyle:"dotted" }
                      }, v.cve_id)
                    ),
                    visibleCols["cvss_score"] !== false && React.createElement("td", { style: { padding: "8px", fontFamily: "monospace", fontSize: 11, fontWeight: 600, color: SEVERITY_COLOR[riskLabel(v.cvss_score)] } }, v.cvss_score),
                    visibleCols["severity"] !== false && React.createElement("td", { style: { padding: "8px" } }, React.createElement(SeverityBadge, { label: v.severity || riskLabel(v.cvss_score) })),
                    visibleCols["tags"] !== false && React.createElement("td", { style: { padding: "8px" } },
                      React.createElement("div", { style: { display: "flex", gap: 3 } },
                        v.is_kev && React.createElement(KEVBadge),
                        v.kev_ransomware === "Known" && React.createElement(RansomwareBadge)
                      )
                    ),
                    visibleCols["status"] !== false && React.createElement("td", { style: { padding: "8px" } }, React.createElement(StatusBadge, { status: v.status || "unreviewed" })),
                    React.createElement("td", { style: { padding: "8px", fontSize: 10, color: "#8a9ab0", whiteSpace: "nowrap" } },
                      visibleCols["first_detected"] !== false && (v.first_detected ? new Date(v.first_detected).toLocaleDateString() : "—")
                    ),
                    React.createElement("td", { style: { padding: "8px", fontSize: 10, color: "#8a9ab0", whiteSpace: "nowrap" } },
                      visibleCols["last_detected"] !== false && (v.last_detected ? new Date(v.last_detected).toLocaleDateString() : "—")
                    ),
                    React.createElement("td", { style: { padding: "8px", fontSize: 11, color: "#4a5568", maxWidth: 220, lineHeight: 1.4 } },
                      visibleCols["required_action"] !== false && (v.kev_required_action || (v.description || "—").slice(0,120))
                    ),
                    visibleCols["triage"] !== false && React.createElement("td", { style: { padding: "8px" } },
                      React.createElement("button", { onClick: () => setTriageVuln(v),
                        style: { padding: "4px 10px", borderRadius: 5, border: "1px solid #e2e8f0", background: "#fff", color: "#2b7cd3", fontSize: 10, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }
                      }, "Triage")
                    )
                  )
                )
              )
            )
          )
        ),

        // ── PORTS TAB ──
        tab === "ports" && React.createElement("div", { style: { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,.04)" } },
          React.createElement("div", { style: { padding: "14px 18px 0" } },
            React.createElement("div", { style: { fontSize: 13, fontWeight: 600 } }, "Open Ports & Services — " + ports.length + " found")
          ),
          React.createElement("div", { style: { padding: "12px 18px 18px" } },
            React.createElement("table", { style: { width: "100%", borderCollapse: "collapse" } },
              React.createElement("thead", null,
                React.createElement("tr", null,
                  [
  {label:"IP Address", col:"ip_address"},
  {label:"Port",       col:"port"},
  {label:"Protocol",   col:"protocol"},
  {label:"Service",    col:"service"},
  {label:"Version",    col:"version"},
  {label:"Risk",       col:null},
].map(h =>
                    React.createElement("th", {
                      key: h.label,
                      onClick: h.col ? () => toggleSort(h.col) : undefined,
                      style: {
                        textAlign:"left", fontSize:10, fontWeight:600, color:"#8a9ab0",
                        textTransform:"uppercase", letterSpacing:".4px",
                        padding:"0 8px 8px", borderBottom:"1px solid #f1f5f9",
                        cursor: h.col ? "pointer" : "default", userSelect:"none"
                      }
                    }, h.label, h.col && sortIcon(h.col))
                  )
                )
              ),
              React.createElement("tbody", null,
                [...ports].sort(function(a,b){
                  var col=sortState.col, dir=sortState.dir;
                  var av=a[col], bv=b[col];
                  if(!av && av!==0) return 1;
                  if(!bv && bv!==0) return -1;
                  if(typeof av==="number") return dir==="desc"?bv-av:av-bv;
                  return dir==="desc"?String(bv).localeCompare(String(av)):String(av).localeCompare(String(bv));
                }).map((p, i) =>
                  React.createElement("tr", { key: i, style: { borderBottom: "1px solid #f8fafc" },
                    onMouseEnter: e => e.currentTarget.style.background = "#f8fafc",
                    onMouseLeave: e => e.currentTarget.style.background = ""
                  },
                    React.createElement("td", { style: { padding: "8px", fontFamily: "monospace", fontSize: 11 } }, p.ip_address),
                    React.createElement("td", { style: { padding: "8px", fontFamily: "monospace", fontSize: 11, fontWeight: 600, color: p.port === 23 ? "#b91c1c" : p.port === 445 ? "#c2410c" : "#0f1923" } }, p.port),
                    React.createElement("td", { style: { padding: "8px", fontSize: 11 } }, p.protocol || "tcp"),
                    React.createElement("td", { style: { padding: "8px", fontSize: 11 } }, p.service || "—"),
                    React.createElement("td", { style: { padding: "8px", fontSize: 11, color: "#4a5568" } }, p.version || "—"),
                    React.createElement("td", { style: { padding: "8px" } },
                      p.port === 23 ? React.createElement("span", { style: { fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 4, background: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca" } }, "⚠ Insecure") :
                      p.port === 445 || p.port === 5900 ? React.createElement("span", { style: { fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 4, background: "#fff7ed", color: "#c2410c", border: "1px solid #fed7aa" } }, "Review") :
                      p.port === 22 ? React.createElement("span", { style: { fontSize: 10, fontWeight: 500, padding: "2px 7px", borderRadius: 4, background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0" } }, "Normal") :
                      React.createElement("span", { style: { fontSize: 10, color: "#8a9ab0" } }, "Monitor")
                    )
                  )
                )
              )
            )
          )
        ),

        // ── THREAT INTEL TAB ──
        tab === "intel" && React.createElement("div", null,
          React.createElement("div", { style: { marginBottom: 16 } },
            React.createElement("div", { style: { fontSize: 13, fontWeight: 600, color: "#0f1923", marginBottom: 4 } }, "Threat Intelligence Briefing"),
            React.createElement("div", { style: { fontSize: 12, color: "#8a9ab0" } }, "Live CISA KEV alerts · Annual industry reports · Updated daily")
          ),
          React.createElement("div", { style: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 } },
            intel.map((item, i) =>
              React.createElement("div", {
                key: i,
                onClick: () => item.url && window.open(item.url, "_blank"),
                style: { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "16px 18px", cursor: item.url ? "pointer" : "default",
                  boxShadow: "0 1px 3px rgba(0,0,0,.04)", transition: "box-shadow .12s",
                  borderLeft: "3px solid " + (item.severity === "critical" ? "#b91c1c" : item.severity === "high" ? "#c2410c" : "#2b7cd3") },
                onMouseEnter: e => e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,.08)",
                onMouseLeave: e => e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,.04)"
              },
                React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8, marginBottom: 8 } },
                  React.createElement("span", { style: { fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4,
                    background: item.severity === "critical" ? "#fef2f2" : item.severity === "high" ? "#fff7ed" : "#eff6ff",
                    color: item.severity === "critical" ? "#b91c1c" : item.severity === "high" ? "#c2410c" : "#2b7cd3",
                    border: "1px solid " + (item.severity === "critical" ? "#fecaca" : item.severity === "high" ? "#fed7aa" : "#bfdbfe") } }, item.source),
                  item.published_at && React.createElement("span", { style: { fontSize: 10, color: "#8a9ab0" } }, item.published_at),
                  item.url && React.createElement("i", { className: "ti ti-external-link", style: { fontSize: 11, color: "#8a9ab0", marginLeft: "auto" } })
                ),
                React.createElement("div", { style: { fontSize: 13, fontWeight: 600, color: "#0f1923", marginBottom: 6, lineHeight: 1.3 } }, item.title),
                React.createElement("div", { style: { fontSize: 12, color: "#4a5568", lineHeight: 1.5 } }, item.summary)
              )
            )
          )
        ),

        // ── HISTORY TAB ──
        tab === "history" && React.createElement("div", { style: { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,.04)" } },
          React.createElement("div", { style: { padding: "14px 18px 0" } },
            React.createElement("div", { style: { fontSize: 13, fontWeight: 600 } }, "Scan History")
          ),
          React.createElement("div", { style: { padding: "12px 18px 18px" } },
            React.createElement("table", { style: { width: "100%", borderCollapse: "collapse" } },
              React.createElement("thead", null,
                React.createElement("tr", null,
                  ["Scan Type","Completed","Assets Found","Vulnerabilities Found"].map(h =>
                    React.createElement("th", {
                      key: h.label,
                      onClick: h.col ? () => toggleSort(h.col) : undefined,
                      style: {
                        textAlign:"left", fontSize:10, fontWeight:600, color:"#8a9ab0",
                        textTransform:"uppercase", letterSpacing:".4px",
                        padding:"0 8px 8px", borderBottom:"1px solid #f1f5f9",
                        cursor: h.col ? "pointer" : "default", userSelect:"none"
                      }
                    }, h.label, h.col && sortIcon(h.col))
                  )
                )
              ),
              React.createElement("tbody", null,
                history.map((s, i) =>
                  React.createElement("tr", { key: i, style: { borderBottom: "1px solid #f8fafc" } },
                    React.createElement("td", { style: { padding: "9px 8px", textTransform: "capitalize", fontSize: 12, fontWeight: 500 } }, s.scan_type),
                    React.createElement("td", { style: { padding: "9px 8px", fontSize: 12, color: "#4a5568" } }, s.completed_at ? new Date(s.completed_at).toLocaleString() : "—"),
                    React.createElement("td", { style: { padding: "9px 8px", fontFamily: "monospace", fontSize: 12 } }, s.assets_found || "—"),
                    React.createElement("td", { style: { padding: "9px 8px", fontFamily: "monospace", fontSize: 12 } }, s.vulnerabilities_found || "—")
                  )
                )
              )
            )
          )
        ),

        tab === "shadow" && React.createElement(ShadowAITab, { token: token }),

        tab === "settings" && React.createElement(SettingsPage, { token: token }),
        tab === "schedule" && React.createElement(SchedulePage, { token: token }),
        // Config stubs
        ["detectors","users","integrations"].includes(tab) && React.createElement("div", { style: { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "40px", textAlign: "center", boxShadow: "0 1px 3px rgba(0,0,0,.04)" } },
          React.createElement("div", { style: { fontSize: 36, marginBottom: 12 } }, "⚙️"),
          React.createElement("div", { style: { fontSize: 16, fontWeight: 600, color: "#0f1923", marginBottom: 8 } }, tab.charAt(0).toUpperCase() + tab.slice(1)),
          React.createElement("div", { style: { fontSize: 13, color: "#8a9ab0" } }, "Configuration coming in cloud version — DigitalOcean deployment")
        )
      )
    ),

    // CVE Detail Panel
    cveDetail && React.createElement(CVEDetailPanel, {
      vuln: cveDetail,
      onClose: () => setCveDetail(null)
    }),

    // Triage modal
    triageVuln && React.createElement(TriageModal, {
      vuln: triageVuln,
      token: token,
      onClose: () => setTriageVuln(null),
      onSaved: fetchData
    })
  );
}

// Root App
function App() {
  var ts = useState(localStorage.getItem("sc_token") || ""); var token = ts[0]; var setToken = ts[1];
  var us = useState(localStorage.getItem("sc_user") || ""); var username = us[0]; var setUsername = us[1];
  var ms = useState(null); var mfaPartial = ms[0]; var setMfaPartial = ms[1];
  var ss = useState(false); var needsMfa = ss[0]; var setNeedsMfa = ss[1];

  function handleLogin(t, u, mfaSetup) { setToken(t); setUsername(u); if (mfaSetup) setNeedsMfa(true); }
  function handleLogout() { localStorage.removeItem("sc_token"); localStorage.removeItem("sc_user"); setToken(""); setUsername(""); setMfaPartial(null); setNeedsMfa(false); }
  function handleMFASuccess(t, u) { localStorage.setItem("sc_token", t); localStorage.setItem("sc_user", u); setToken(t); setUsername(u); setMfaPartial(null); }

  if (!token && !mfaPartial) return React.createElement(LoginScreen, { onLogin: handleLogin, onMFARequired: pt => setMfaPartial(pt) });
  if (mfaPartial) return React.createElement(MFAVerifyScreen, { partialToken: mfaPartial, onSuccess: handleMFASuccess, onBack: () => setMfaPartial(null) });
  if (needsMfa) return React.createElement(MFASetupScreen, { token, onComplete: () => setNeedsMfa(false), onSkip: () => setNeedsMfa(false) });
  return React.createElement(Dashboard, { token, username, onLogout: handleLogout });
}

export default App;
